import globalLang from 'shared/lang/en';
import {
  aggregationMethod,
  azureDevOpsError,
  dataSourceQueryErrorSource,
  dataSourceQueryGenericError,
  goalProgressMeasurement,
  jiraError,
  msPlannerError,
  msPowerBiError,
  printableExportFormat,
  printablePage,
  progressDisplayOption,
  spreadsheetError,
} from 'shared/constants.json';
import { errorType, samlLoginStatus } from '@/constants/sentinel.json';
import { importValidationFailure } from '@/constants/bumblebee.json';
import { mergeLangObjects } from 'shared/lang/lang';

/* eslint-disable max-len */
const lang = {
  views: {
    home: 'Home',
    profile: 'Profile',
    profileSettings: 'Profile - Settings',
    profileAccess: 'Access',
    myNotifications: 'Notifications',
    accountSettings: 'Workspace - Settings',
    login: 'Log in',
    register: 'Signup',
    registerLandingPage: 'Workspace - Log in',
    userList: 'Members',
    spaceList: 'Teams',
    userDetail: 'Employees',
    userAdd: 'Add Employee',
    properties: 'Properties',
    documentList: 'Dashboards',
    accessGroupDetails: 'Access groups',
    setPasswordTitle: 'Reset your password',
    passwordForgetTitle: 'Forgot password',
    goalCycles: 'Cycles',
    goalDetails: 'Goal Details',
    goalSettings: 'Goal Settings',
    settings: 'Settings',
    notFound: '404',
    goalsAtlas: 'Network',
    profileInbox: 'Inbox',
    optionMembers: 'Members',
    dashboards: 'Dashboards',
    goalInsights: 'Dashboard',
    dashboardDetails: 'Dashboard',
    accountSpace: 'Company',
    spaceDetails: 'Team',
    onboarding: 'Onboarding',
    publishedView: 'Goals',
    integrationSettings: 'Integrations',
    manageDataSource: 'Manage integration',
    goalsUpdateFeed: 'Feed',
    plans: 'Plans',
    goalTypes: 'Goal types',
    goalStatus: 'Status',
    authorizationCodeGrantRedirect: 'Sign in',
    aiChat: 'AI chat',
  },
  aiChat: {
    title: 'AI chat',
    newChat: 'New chat',
    newChatTitle: 'New AI chat',
    untitled: 'Untitled chat',
    titleLabel: 'Title',
    history: 'Chat history',
    noChats: 'No chats yet',
    noResults: 'No chats match your search',
    searchPlaceholder: 'Search chats...',
    emptyState: 'Ask the assistant anything about your workspace.',
    deletePrompt: 'Delete this chat? This cannot be undone.',
    inputPlaceholder: 'Ask anything about your workspace...',
    send: 'Send',
    copyMessage: 'Copy message',
    retry: 'Try again',
    stop: 'Stop',
    stopped: 'Answer stopped',
    pin: 'Pin chat',
    unpin: 'Unpin chat',
    openFullScreen: 'Open full screen',
    exitFullScreen: 'Back to side panel',
    attach: {
      title: 'Attach files',
      subTitle: 'Add files to your message',
    },
    attachmentAlreadyAdded: '{name} is already attached',
    attachmentUnreadable: {
      image: 'The assistant cannot read {name}. Save it as PNG or JPEG and attach it again.',
      legacyOffice: 'The assistant cannot read {name}. Save it as .docx, .xlsx or .pptx and attach it again.',
      richText: 'The assistant cannot read {name}. Save it as .docx or .txt and attach it again.',
      archive: 'The assistant cannot open {name}. Attach the files inside it instead.',
      other: 'The assistant cannot read {name}.',
    },
    attachmentLimitReached: 'You can attach up to {limit} files to a message',
    thinking: 'Thinking...',
    failure: {
      notSent: 'Your message could not be sent.',
      unanswered: 'No answer came back.',
    },
    picker: {
      model: 'Model',
      default: 'Default',
      effort: 'Effort',
      webSearch: 'Web search',
      internalHint: 'Only shown for internal testing',
      efforts: {
        low: 'Low',
        medium: 'Medium',
        high: 'High',
      },
    },
    groups: {
      pinned: 'Pinned',
      today: 'Today',
      yesterday: 'Yesterday',
      previous30Days: 'Previous 30 days',
      older: 'Older',
    },
  },
  fileType: {
    archive: '{extension} archive',
    code: '{extension} file',
    document: '{extension} document',
    generic: '{extension} file',
    image: '{extension} image',
    pdf: 'PDF document',
    presentation: '{extension} presentation',
    spreadsheet: '{extension} spreadsheet',
    text: '{extension} file',
    unknown: 'File',
  },
  highcharts: {
    decimalPoint: '.',
    thousandsSep: ',',
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    actual: 'Actual',
    expected: 'Expected',
    trend: 'Trend',
    noData: 'No data available',
    noDataShort: 'No data',
  },
  navigation: {
    home: 'Home',
    settings: 'Settings',
    logout: 'Log out',
    feedback: 'Feedback',
    users: 'Members',
    about: 'About',
    explorer: 'Explorer',
    goalInsights: 'Dashboard',
    goalInsightsPerformance: 'Progress',
    goalInsightsHealth: 'Health',
    goalInsightsPerformanceTitle: 'Progress dashboard',
    goalInsightsHealthTitle: 'Health dashboard',
    dashboards: 'Dashboards',
    goalsExplorerCascade: 'Cascade',
    goalsExplorerList: 'List',
    goalsAtlas: 'Network',
    updatesExplorer: 'Check-ins',
    search: 'Search',
    feed: 'Feed',
    updatesExplorerList: 'List',
    updateSchedules: 'Check-in schedules',
    updateScheduleDetails: 'Check-in Schedule Details',
    updateTemplates: 'Check-in Templates',
    plannings: 'Plans',
    planningDetails: 'Plan',
    newDashboard: 'New Dashboard',
    dashboard: 'Dashboards',
  },
  mainNavigation: {
    tools: 'Tools',
    macSearchKeyBinding: '⌘K',
    searchKeyBinding: 'Ctrl + K',
    inbox: 'Inbox',
    favorites: 'Favorites',
    spaceDropdownTitle: 'Space',
    invitePeople: 'Invite members',
    invite: 'Invite',
    onboarding: 'Onboarding',
    helpAndSupport: 'Help',
    myWorkspace: 'Spaces',
    insights: 'Insights',
    trash: 'Trash',
  },
  trashContent: {
    title: 'Deleted items',
    tabGoals: 'Goals',
    noItems: { goal: 'No deleted goals', filter: 'No filter results' },
    action: {
      confirmDeletePermanently: 'Are you sure you want to delete the goal "{title}" permanently?',
      yesDelete: 'Yes. Delete this goal',
    },
    openGoal: 'Open goal',
  },
  myWorkspace: {
    home: 'Home',
    viewAll: 'View all',
  },
  settingsNavigation: {
    title: 'Settings',
    preferences: 'Settings',
    account: 'Account',
    general: 'General',
    workspace: 'Workspace',
    member: 'User',
    features: 'Tools',
    profile: 'Profile',
    userList: 'Members',
    spaceList: 'Teams',
    userFields: 'Properties',
    accessControl: 'Access groups',
    goals: 'Goals',
    profileAccess: 'Access',
    myNotifications: 'Notifications',
    identityAndProvisioning: 'Identity and provisioning',
    plans: 'Plans and billing',
    goalSettings: 'Settings',
    goalCycles: 'Cycles',
    goalTypes: 'Types and rules',
    goalStatusSettings: 'Status',
  },
  edges: {
    status: 'Status',
    title: 'Title',
    createdAt: 'Date created',
    modifiedAt: 'Date modified',
    cachedLastUpdatedAt: 'Last updated',
    cachedCalculatedCurrentBounded: 'Progress',
    cachedCalculatedCurrent: 'Progress',
    cachedGoalCycle: 'Cycle',
    goalCycle: 'Cycle',
    cachedStatus: 'Status',
    creator: 'Creator',
    date: 'Date',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    customCreatedAt: 'Date created',
  },
  pagination: {
    rowsPerPage: 'Rows per Page',
    from: 'of',
  },
  editPropertyForm: {
    editTitle: 'Edit property',
    createTitle: 'Create a new property',
    label: 'Name',
    description: 'Description',
    labelHint: 'The name should not be too long.',
    deletePrompt: 'Do you really want to delete this property?',
    deletePromptButton: 'Yes, delete',
    type: 'Type',
    usedFor: 'Usage',
    usedForDescription: 'Set here for which resources this property should be used. It is also possible to use properties for several resources, e.g. the property "Department" for Goals and Users.',
    numberFormat: 'Formatting options',
    formatting: 'Formatting options',
    dateFormat: 'Date format',
    timeFormat: 'Time format',
    dateFormats: {
      full: 'Full date',
      short: 'Short date',
      monthDayYear: 'Month/Day/Year',
      dayMonthYear: 'Day/Month/Year',
      yearMonthDay: 'Year/Month/Day',
      relative: 'Relative',
    },
    timeFormats: {
      hidden: 'Hidden',
      hour24: '24 hour',
    },
    segments: 'Options',
    segmentsDescription: 'Options are the concrete values of a property, e.g. the departments of your company.',
    addSegment: 'Add Option',
    deleteText: 'Are you sure that you want to delete this option?',
    number: 'Number',
    commaSeparated: 'Comma separated number',
    percent: 'Percent',
    euro: 'Euro',
    dollar: 'Dollar',
    pound: 'Pound',
    lookupValue: 'Lookup value',
    lookupDescription: 'Specify the goal and user property which are used to retrieve the lookup value.',
    lookupUsedForHint: 'Lookup properties can only be used for goals.',
    lookupRelationsNotSet: 'The lookup value needs to be set.',
    spaceManagementHint: 'Looking for teams? They can now be found on their own page under {link}.',
    spaceManagementLink: '@:{\'settingsNavigation.workspace\'} / @:{\'settingsNavigation.spaceList\'}',
  },
  properties: {
    pageTitle: 'Properties',
    pageSubtitle: 'Properties enrich your resources such as goals or users with additional information. It allows you to customize the platform to best fit your company and processes.',
    addButtonLabel: 'Add a property',
    labelTitle: 'Name',
    labelType: 'Type',
    labelStatus: 'Status',
    labelUsedFor: 'Usage',
    createNewTitle: 'Create a new property',
    editTitle: 'Edit Property',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/properties',
    help: 'Learn more about how you can use properties.',
  },
  propertyConstants: {
    space: 'Space',
    options: 'Multi-Select',
    singleSelect: 'Select',
    text: 'Text',
    date: 'Date',
    number: 'Number',
    user: 'Person',
    url: 'Link',
    lookup: 'Lookup',
    goal: 'Goals',
    update: 'Check-ins',
    inactive: 'Inactive',
    active: 'Active',
    spaceDescription: 'Select multiple teams',
    optionsDescription: 'Select multiple options, e.g. departments',
    singleSelectDescription: 'Select a single option, e.g. a status',
    textDescription: 'A custom text, e.g. the user\'s position',
    dateDescription: 'A concrete date, e.g. a user\'s birthday',
    userDescription: 'A relation between persons, e.g. manager',
    urlDescription: 'A link to a page on the web',
    numberDescription: 'A number, e.g. salary or performance score',
    lookupDescription: 'Returns the value of a property from another resource (e.g. User)',
  },
  editUserForm: {
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    language: 'Language',
    createTitle: 'Add user',
    editTitle: 'Edit user',
    groups: {
      personal: 'Personal data',
      other: 'Other properties',
    },
    timezone: 'Time zone',
  },
  userAdd: { title: 'Add user' },
  userList: {
    title: 'Members',
    tableTitle: 'Members',
    active: 'Active',
    archived: 'Deactivated',
    invite_pending: 'Invite pending',
    no_invitation: 'Not invited yet',
    status: 'Status',
    userProvisioning: 'Automatic provisioning',
    accessGroups: 'Access groups',
    activatedAt: 'Activated on',
    deactivatedOn: 'Deactivated on',
    invitedByAt: 'Invited by {name} on',
    invitedAt: 'Invited at',
    invitedByYouAt: 'Invited by you on',
    add: 'Add user',
    deleteUserTitle: 'Are you sure that you want to delete this user?',
    archiveUserTitle: 'Are you sure that you want to deactivate this user?',
    disableUserProvisioningTitle: 'Are you sure you want to pause user provisioning?',
    importModal: {
      header: 'Import from CSV',
      text: 'Import a CSV file to add employees to Mooncamp. Your CSV should include a header row with field names that match your property names. You can download an example {link}.',
      here: 'here',
      fileSelect: 'Choose file',
      submit: 'Upload',
      ignoreDeactivated: {
        label: 'Ignore deactivated users',
        tooltip: 'When this is checked, rows in the CSV that match with a deactivated user will not be imported. Otherwise, the row will be imported and the user reactivated.',
      },
      warnings: 'Warnings',
      errors: 'Errors',
      row: 'ln. {row}',
      emptyValue: '<empty>',
    },
    importModalButton: 'Import CSV',
    csvDisabled: 'Feature not available during trial period.',
    trialUserLimitReached: 'Trial workspaces can have up to {limit} users (excluding demo users). Contact support to increase this limit.',
    deleteSubmitLabel: 'Delete',
    exportUsers: 'Export CSV',
    bulkInvitationBox: {
      text: 'There are {users} which have not been invited yet. They will not receive any emails from Mooncamp until they log in at least once to activate their account.',
      userCount: '{userCount} users',
      bulkAction: 'Send invitation to {userCount} users',
      goToMembers: 'Go to @:{\'settingsNavigation.userList\'}',
    },
    bulkInvitationDialog: {
      text: 'Please confirm that you want to send an invitation email to {userCount} users with the status "Not invited yet".',
      confirmAction: 'Yes, send invitations to {userCount} users',
    },
    demo: 'Demo user',
    removeDemoUsers: {
      text: 'Your workspace still contains demo users. You should delete them before you actively start using Mooncamp with your team.',
      button: 'Delete demo users',
      confirmText: 'Are you sure?',
      confirmAction: 'Delete demo users',
    },
    filters: {
      active: 'Active',
      archived: 'Deactivated',
      none: 'All',
    },
  },
  userListCard: {
    name: 'Name',
    editUser: 'Edit User',
    sendInvitationMailConfirm: 'Send an invitation to {firstname} {lastname}?',
    sendInvitationMail: 'Send invitation',
    invitationSuccess: 'Invitation successfully sent',
    pending: 'Invitation pending',
    deactivateTooltip: 'Deactivated users can no longer log in, be assigned as goal owners, or appear in user selections – however, their existing data remains preserved and accessible',
    restoreDisabled: 'User is controlled via the Identity Provider. Disable automatic provisioning if you want to restore the user.',
    disableUserProvisioningTooltip: 'Users with paused provisioning will no longer be synchronized with the identity provider via SCIM',
  },
  userProvisioningStatusBadge: {
    enabled: 'Enabled',
    disabled: 'Disabled',
    notAvailable: 'Not synchronized',
    notAvailableTooltip: 'For this user no connection is available to the identity provider via SCIM. This user may have been created manually, or the identity provider permanently removed the user via SCIM.',
    status: 'Provisioning',
    provisionedAt: 'Automatically provisioned since',
    deactivatedSince: ' Automatic provisioning paused since',
    disableProvisioning: 'Pause automatic provisioning',
    enableProvisioning: 'Enable automatic provisioning',
    remotelyDisabled: 'User was marked as inactive by the identity provider',
  },
  spaceStatus: {
    active: 'Active',
    archived: 'Archived',
    joined: 'Joined',
  },
  spaceList: {
    learnMore: 'Learn more about teams.',
    helpCenterLink: '@:{\'general.helpCenterBaseUrl\'}/teamspaces',
    title: 'Teams',
    tableTitle: 'Teams',
    create: 'Create team',
    edit: 'Edit team',
    filters: {
      active: 'Active',
      archived: 'Archived',
      joined: 'My Teams',
      all: 'All',
    },
  },
  spaceListTable: {
    header: {
      name: 'Name',
      members: 'Members',
      status: 'Status',
    },
    archivedAt: 'Archived at',
  },
  spaceTopBarMenu: { members: '{count} member | {count} members' },
  accountPicker: {
    addNewAccount: 'Add new workspace',
    invitationNotAccepted: 'Pending',
    untitledAccount: 'Untitled workspace',
  },
  addAccountDialog: {
    title: 'Create a new workspace',
    create: 'Create workspace',
  },
  spacesContextMenu: {
    openSpace: 'Open team',
    createSubSpace: 'Create subteam',
    cannotCreate: 'You are not allowed to create teams.',
    cannotCreateMultiple: 'You are not allowed to create teams with multiple parents.',
    cannotCreateLevel: 'Only {maxLevel} levels of teams can be created.',
    cannotDelete: 'You are not allowed to delete the selected item.',
    cannotDeleteMultiple: 'You are not allowed to delete multiple items.',
    archiveSomeHint: 'Some teams are already archived.',
  },
  spaceEditor: {
    create: 'Create team',
    edit: 'Team settings',
    dangerZoneLabel: 'Actions',
    generalTab: 'General',
    membersTab: 'Members {count}',
    permissionsTab: 'Permissions',
    archiveText: 'Archive team',
    archiveSubtext: 'Remove team from sidebar, goals will not be deleted',
    restoreText: 'Restore team',
    restoreSubtext: 'Restore team to sidebar',
    deleteText: 'Delete team',
    deleteSubtext: 'Remove team and all its goals for all members',
    deleteModal: {
      title: 'Are you sure that you want to delete "{optionTitle}"?',
      message: 'Goals exclusively associated to this team will be deleted.\nGoals shared with other teams will be preserved.\n\nThe data cannot be restored, be careful!',
      securityQuestion: 'Type in the team name to proceed',
      okText: 'Delete team',
    },
    validation: {
      titleIsEmpty: 'The team name cannot be empty.',
      titleAlreadyExists: 'This team name already exists.',
      circularReference: 'The teams you are trying to align form a circular reference, which is not permitted.',
    },
  },
  spaceGeneralForm: {
    generalLabel: 'Icon, color and name',
    parentsLabel: 'Parent team',
    descriptionLabel: 'Description',
    titlePlaceholder: 'Enter name...',
    parentsPlaceholder: 'No team selected',
  },
  spaceMembers: {
    addMembers: 'Add members',
    memberRole: 'Member',
    header: {
      name: 'Name',
      role: 'Role',
    },
    removeMemberPrompt: 'Are you sure you want to remove the member from the team?',
    removeRolePrompt: 'Are you sure you want to remove yourself from this role? You might lose permissions needed to edit this team.',
  },
  spaceMembershipSelector: {
    addMembersTo: 'Add members to',
    addMore: 'Add more',
  },
  spacePermissions: {
    permissions: 'Permissions',
    disabledSpaceMemberAccessType: 'Team members can\'t have lower access than workspace members',
    owners: {
      title: 'Team owners',
      subtitle: 'Team owners have full access to team settings and resources associated with the team by default',
    },
    members: {
      title: 'Team members',
      subtitle: 'Members access to resources associated with the team',
    },
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/access-rights',
    learnMore: 'Learn more about teamspace permissions.',
    accountAccessTitle: 'Everyone else in {name}',
    addGroupOrUser: 'Add',
  },
  documentList: { createDocument: 'Create Document' },
  createDocumentCard: {
    nameLabel: 'Name',
    heading: 'Create a new document',
  },
  gridItemContent: {
    turnInto: 'Turn into',
    h1: 'Heading 1',
    h2: 'Heading 2',
    h3: 'Heading 3',
    text: 'Text',
    handleHint: '<strong>Drag</strong> in order to move',
    replace: 'Replace',
    settings: 'Settings',
  },
  accessGroupDetails: {
    addGroup: 'Add access group',
    noAccessGroups: 'No access groups have been created yet.',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/rights-and-access-control',
    help: 'Learn more about rights and access control.',
  },
  passwordForget: { header: 'Reset your Password' },
  passwordForgetForm: {
    hint1: 'Please type in the email, that you used for your registration.',
    buttonText: 'Send me the link',
    success: 'Help is on the way! Please have a look in your email inbox.',
    error: 'Oops, an error occurred. Please check your email and try again.',
  },
  setPassword: { header: 'Choose a new password' },
  setPasswordForm: {
    passwordLabel: 'New Password',
    tip: 'Tip',
    hint: 'Your password should consist of at least eight characters and should include special characters like *, %, $, ? or &.',
    buttonText: 'Set new password',
    success: 'Your password has successfully been set. Please log in again.',
    noLongerValid: 'This link is no longer valid.',
  },
  rRulePicker: {
    first: '1st',
    second: '2nd',
    third: '3rd',
    fourth: '4th',
    last: 'last',
    at: 'At',
    on: 'On',
    occurrence: 'Next execution | Next {amount} executions',
    once: 'One-time',
    startDateDescription: 'Start date of the reminder',
    daily: 'Daily',
    weeklyOn: 'Every week on',
    monthlyOn: 'Monthly on',
    yearlyOn: 'Yearly on',
    custom: 'Add custom option',
    customConfig: 'Custom settings',
    repeatEvery: 'Repeat every ',
    day: 'Day | Days',
    week: 'Week | Weeks',
    month: 'Month | Months',
    year: 'Year | Years',
    repeatAt: 'Repeat on',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    end: 'End',
    never: 'Never',
    after: 'After',
    executions: 'Executions',
    startTimeHint: 'The start time needs to be in the future.',
    startDate: 'Start date',
    recurrencePattern: 'Recurrence',
  },
  userScopeTreeUserList: { userList: 'Selected users' },
  durationPicker: {
    minute: 'Minute | Minutes',
    hour: 'Hour | Hours',
    day: 'Day | Days',
  },
  timeDifference: {
    minute: 'Minute | Minutes',
    hour: 'Hour | Hours',
    day: 'Day | Days',
    afterInitialMail: 'after the initial notification',
  },
  mDatePickerInput: { afterToday: 'The date needs to be in the future.' },
  tableHeaderMenuCell: {
    filter: 'Filter',
    sortDescending: 'Sort descending',
    sortAscending: 'Sort ascending',
    hideInView: 'Hide in view',
  },
  userScopeFilter: {
    remove: 'Remove',
    where: 'Where',
    group: 'Turn into group',
    turnIntoFilter: 'Turn into filter',
    addGroupFilter: 'Add filter group',
    wrapInGroupSubTitle: 'Create a filter group for this',
    resolveGroup: 'Resolve group',
    wrapInGroup: 'Wrap in group',
    addFilter: 'Add a filter',
    empty: 'There is no active filter',
  },
  accountScopeFilter: {
    all: 'All users',
    segments: 'Specific segments',
  },
  leafNode: {
    placeholder: 'Choose...',
    deleteTooltip: 'Remove Filter',
  },
  dateScope: {
    before: 'Before',
    after: 'After',
    isBefore: 'Is before',
    isAfter: 'Is after',
    isBetween: 'Is within',
    isEmpty: 'Is empty',
    isNotEmpty: 'Is not empty',
    today: 'Today',
    exactDate: 'Exact date',
    nextXDays: 'Next X days',
    lastXDays: 'Last X days',
    nextNDays: 'Next {value} days',
    lastNDays: 'Last {value} days',
    withinLastNDays: 'Within last {value} days',
    withinNextNDays: 'Within next {value} days',
    thisWeek: 'This week',
    withinThisWeek: 'Within this week',
    lastWeek: 'Last week',
    withinLastWeek: 'Within last week',
    thisMonth: 'This month',
    withinThisMonth: 'Within this month',
    lastMonth: 'Last month',
    withinLastMonth: 'Within last month',
    thisQuarter: 'This quarter',
    withinThisQuarter: 'Within this quarter',
    lastQuarter: 'Last quarter',
    withinLastQuarter: 'Within last quarter',
    thisYear: 'This year',
    withinThisYear: 'Within this year',
    lastYear: 'Last year',
    withinLastYear: 'Within last year',
    allTime: 'All time',
    dateRange: 'Date range',
    xDaysFromNow: 'X days from now',
    xDaysAgo: 'X days ago',
    nDaysFromNow: '{value} days from now',
    nDaysAgo: '{value} days ago',
    beginningThisWeek: 'Beginning of this week',
    beginningLastWeek: 'Beginning of last week',
    beginningThisMonth: 'Beginning of this month',
    beginningLastMonth: 'Beginning of last month',
  },
  optionScope: {
    not: 'Is not',
    is: 'Is',
    contains: 'Contains',
    notContains: 'Does not contain',
    isEmpty: 'Is empty',
    isNotEmpty: 'Is not empty',
    placeholder: 'Choose...',
  },
  userScope: {
    staticUserLabel: 'User',
    placeholder: 'Select one or several persons',
  },
  profileLayout: {
    hoohoo: 'Hohoo! Hohoo!',
    goodEvening: 'Good evening',
    goodMorning: 'Good morning',
    hello: 'Hello',
    users: 'Employees',
  },
  goalCycleEditor: {
    setup: 'Setup',
    notifications: 'Notifications',
    verify: 'Verify',
    subTitle: 'A goal cycle is used for grouping your company goals within a set timeframe. Please select the name that will be displayed to your employees.',
    verifySubTitle: 'You\'re all set! Double check the summary of your configurations and review your settings before creating the new goal cycle.',
    notificationSubTitle: 'Notify your colleagues about the start of the new goal cycle.',
    deactivate: 'Deactivate',
  },
  cycleSetup: {
    cycleNameLabel: 'Name of the cycle',
    datesHeading: 'Start and end date',
    subHeading: 'Set the start and end date of this cycle.',
  },
  goalsExplorer: {
    list: 'List',
    goalsAtlas: 'Network',
    cascade: 'Cascade',
    properties: 'Properties',
    sort: 'Sort',
    allCycles: 'All Cycles',
    explorer: 'Explorer',
    feed: 'Feed',
  },
  goal: {
    status: { unpublished: 'Draft' },
    detailHeader: {
      allGoals: 'All {title}',
      shareTitle: 'Visibility',
      custom: 'Custom',
      deletePrompt: 'Are you sure that you want to delete the selected {title}?',
      notification: 'Notifications',
      addToDashboard: 'Add to dashboard',
      disableStatusAutoUpdate: 'Automated status update',
      statusUpdateTooltip: 'Automatically update the status based on this goal’s subitems.',
    },
    goalEditor: {
      progressMeasurement: 'Progress',
      description: 'Description',
      parent: 'Parent Alignment',
      goalCycle: 'Cycle',
      binary: 'Open/Done',
      metric: 'Metric',
      start: 'Start',
      end: 'End',
      addDescription: 'Add Description',
    },
  },
  pageHeader: { openAsPage: 'Open', share: 'Share' },
  goalDetails: {
    keyResults: 'Key Results',
    addKeyResult: 'Add Key Result',
    updates: 'Activity',
    updatesHiddenByFilter: '1 activity is hidden by the current filter. | {count} activities are hidden by the current filter.',
    allUpdatesHiddenByFilter: 'All activities are hidden by the current filter.',
    deleteEmptyPrompt: 'This {title} is still empty. Would you like to discard it?',
    deleteOkText: 'Yes, discard it',
    deleteCancelText: 'No, keep it',
  },
  goalsFilter: {
    title: 'Name',
    description: 'Description',
    progress: 'Progress',
    parent: 'Parent alignment',
    children: 'Subitem',
    goalIs: 'Goal',
    status: 'Status',
    current: 'Current value',
    cycle: 'Cycle',
    lastUpdatedAt: 'Last updated',
    lastGoalActivityMessage: 'Last update comment',
  },
  filterChips: {
    title: 'Filter',
    filter: 'Filter',
    addFilter: 'Add filter',
    deleteFilter: 'Delete filter',
    advancedFilter: 'Advanced filter',
    addAdvancedFilter: 'Add advanced filter',
    rule: 'rule | rules',
  },
  quickFilter: {
    delete: 'Delete filter',
    addToAdvanced: 'Add to advanced filter',
  },
  sorterChips: { sorts: 'sorts' },
  sorterBuilder: {
    add: 'Add sort',
    empty: 'There is no active sorting',
  },
  sortOrder: {
    descending: 'Descending',
    ascending: 'Ascending',
  },
  metricForm: {
    metricFormInfo: 'The start and target value of published goals only consider published subitems.',
    binary: 'Open/Done',
    continuous: 'Start and target',
    threshold: 'Threshold',
    alignedItems: 'Aggregation',
    none: 'None',
    dataSourceDisabledHint: 'You need to publish the goal before you can connect to a data source.',
    startTargetDescription: 'Start and target from subitems',
    alignedItemsDescription: {
      [aggregationMethod.relative]: 'Calculate progress using subitems',
      [aggregationMethod.absolute]: 'Select subitems to sum up',
    },
    start: 'Start',
    end: 'Target',
    metric: 'Unit',
    custom: 'Custom',
    coloring: 'Type',
    notEqual: 'The start and target value must not be the same.',
    notEmpty: 'Value is required.',
    metricMaxLen: 'Length must be less than {length} characters.',
    tooltip: {
      [goalProgressMeasurement.continuous]: 'Goal progress is measured based on a start and target value.',
      [goalProgressMeasurement.alignedItems]: 'For goals whose progress should be measured based on the progress of subitems. Shown as a percentage from 0-100.',
      [goalProgressMeasurement.threshold]: 'For goals that should not exceed or fall below a certain threshold.',
      [goalProgressMeasurement.binary]: 'For goals that are either achieved or not achieved.',
      [goalProgressMeasurement.none]: 'For goals where no progress should be measured.',
    },
    aggregationMethod: {
      [aggregationMethod.relative]: 'Percentage (Relative)',
      [aggregationMethod.absolute]: 'Sum (Absolute)',
    },
  },
  progressMeasurement: {
    measurement: 'Measurement',
    calculateBy: 'Calculate progress by',
  },
  alignedItemAdvancedOptions: {
    trigger: 'Advanced options',
    settings: {
      unboundedProgress: 'Allow overachievement of subitems',
      disableStatusAutoUpdate: 'Enable automatic status update',
    },
    tooltips: {
      unboundedProgress: 'Allow subitems to contribute with more than 100%.',
      disableStatusAutoUpdate: 'Enable to automatically update the goal\'s status whenever the progress of a subitem changes.',
    },
  },
  goalThresholdTargetArea: {
    above: 'Stay above',
    below: 'Stay below',
    aboveOrEqual: 'Stay above or equal',
    belowOrEqual: 'Stay below or equal',
    description: 'Target area is {placement} the line',
    placement: {
      above: 'above',
      below: 'below',
      aboveOrEqual: 'above or on',
      belowOrEqual: 'below or on',
    },
  },
  profileSettings: {
    microsoftSyncStatus: {
      consentUrlError: 'Unfortunately, there was an error during communication with Microsoft.',
      readStateError: 'Unfortunately, there was an error during communication with Microsoft.',
      syncError: 'Unfortunately, there was an error during communication with Microsoft.',
      success: 'Connection was established.',
    },
    connectMicrosoftDescription: 'If you wish to login via any (additional) Microsoft account, you can connect it here. Removing or adding any connection does not affect the login behavior with {email}.',
    deleteMicrosoftEmailPrompt: 'Do you really want to remove the Microsoft connection {email}?',
    title: 'Settings',
    sectionHeading: 'Personal Info',
    setProfilePicture: 'Position and size your photo',
    photo: 'Photo',
    uploadImage: 'Upload',
    deleteImage: 'Delete',
    deleteImagePrompt: {
      content: 'Are you sure that you want to delete your photo?',
      okText: 'Yes, delete',
    },
    leaveAccount: {
      title: 'Leave workspace',
      description: 'Leaving removes you from this workspace. You can be invited back later.',
      promptContent: 'Are you sure you want to leave "{account}"? You will lose access to this workspace.',
      okText: 'Yes, leave',
      lastAdminError: 'You are the last admin of this workspace and cannot leave.',
    },
    connectMicrosoft: 'Connect Microsoft Accounts',
    connectMicrosoftAction: 'Connect Microsoft Account',
    notificationsHeading: 'Notifications',
    deleteMicrosoftEmail: 'Remove',
    slackChannel: 'Channel',
    changeSlackChannel: 'Change Channel',
    activateSlack: 'Activate',
    deactivateSlack: 'Deactivate',
    activateMicrosoft: 'Activate',
    deactivateMicrosoft: 'Deactivate',
    microsoftHintTitle: 'Use Microsoft Teams integration',
    microsoftHint: 'In order to use the Microsoft Teams integration please install the Mooncamp Bot via the Microsoft Teams app store. Further information are available here:',
    slackHintTitle: 'Use Slack integration',
    slackHint: 'In order to use the Slack integration, try talking to the Slack bot! You can start a conversation with it, like with any other user.',
  },
  accountSettings: {
    setCompanyPicture: 'Set a company logo',
    setProfilePicture: 'Choose a profile picture',
    nameTaken: 'The domain is already taken. Please use a different one.',
    clickToCopy: 'Click to copy',
    appIntegration: {
      heading: 'Integrations',
      subHeading: 'Connect Mooncamp with other software services',
      connectSlack: 'Connect with Slack',
      slackConnected: 'Slack connected',
      slackDisconnect: 'Disconnect Slack',
      slackDisconnectText: 'Do you really want to disconnect Slack from Mooncamp?',
      slackConnectionError: 'Error while connecting with Slack',
      notActivated: 'Slack is not installed for this workspace, please contact a workspace admin.',
      notActivatedActivate: 'Activate now',
      slackAlreadyLinked: 'This Slack account is already connected to Mooncamp under a different login. The bot can only be connected to one login — please disconnect there first.',
    },
    title: 'Workspace Settings',
    addLogo: 'Add logo',
    deleteImagePrompt: {
      content: 'Are you sure that you want to delete the logo?',
      okText: 'Yes, delete',
    },
    companyInfo: {
      heading: 'Organization Info',
      nameLabel: 'Name of the Organization',
      signUpPath: 'Invite link',
      signUpPathDescription: 'Share the {link} link to invite people to your workspace.',
      signUpPathThrottle: 'You can change only once every 24 hours.',
      gotIt: 'Got it',
      inviteHint: 'Please share this link only after all users are created within Mooncamp. Otherwise, invited users may unintentionally create new workspaces instead of joining yours.',
    },
    features: {
      heading: 'Tools',
      subHeading: 'Please select the tools that should be available for your workspace.',
      activateGoals: {
        title: 'Activate Goals',
        content: 'Are you sure that you want to activate Goals for all your employees?',
      },
      deactivateGoals: {
        title: 'Deactivate Goals',
        content: 'Are you sure that you want to deactivate Goals for all your employees?',
      },
      activateUpdates: {
        title: 'Activate check-ins',
        content: 'Are you sure that you want to activate check-ins for all your employees?',
      },
      deactivateUpdates: {
        title: 'Deactivate check-ins',
        content: 'Are you sure that you want to deactivate check-ins for all your employees?',
      },
      goalsHeading: 'Goals',
      goalsDescription: 'Goals are a great way to drive transparency and alignment within your organization.',
      updatesHeading: 'Check-ins',
      updatesDescription: 'Check-ins allow you to communicate status and progress updates of your teams.',
      activate: 'Activate',
      deactivate: 'Deactivate',
    },
    reset: {
      heading: 'Reset workspace',
      subHeading: 'You can clear your workspace from existing data, e.g. the demo or testing data.',
      button: 'Open reset menu',
    },
    deleteWorkspace: {
      prompt: 'Delete workspace',
      subHeading: 'Permanently delete this workspace and all of its data.',
      activeAccountHint: 'This workspace has an active subscription and cannot be deleted here. Please contact support to delete it.',
      warningMessage: 'You are about to permanently delete this workspace and everything in it. This cannot be undone. Do you want to continue?',
      confirmQuestion: 'Are you really sure you want to delete the workspace?',
      confirmButton: 'Yes, permanently delete workspace',
    },
    import: {
      heading: 'Import users',
      subheading: 'You can import users via the CSV format. The example shows you how the data needs to be encoded.',
      buttonExample: 'Download example',
      buttonSelectFile: 'Select csv file',
      buttonUpload: 'Upload',
    },
  },
  notification: {
    subject: 'Subject',
    body: 'Message',
    date: 'Sendout date',
    time: 'Time',
    receivers: 'Receivers',
    showParticipants: 'Show Receivers',
    userList: 'Receivers',
    runAt: 'Date',
  },
  goalNotification: {
    addNotification: 'Add Notification',
    deleteNotification: 'Are you sure that you want to delete this notification?',
  },
  app: {
    newVersion: 'A new version of Mooncamp is available. Please reload the page or click here to update the application.',
    foreignLogin: 'You are logged in via foreign login, be careful!',
    tenantAccessRecovered: 'You don’t have access to “{requested}”. Showing “{shown}” instead.',
    noAccess: {
      heading: 'No Access',
      explanation: 'You are not allowed to view this content',
      button: 'Back to main page',
    },
    featureNotAvailable: {
      heading: 'Feature not available',
      explanation: 'This feature is not available within this workspace. Please contact your workspace administrator or our customer support team for help.',
      button: 'Back to main page',
    },
    accountExpired: {
      heading: 'Your workspace has expired',
      explanation: 'Your workspace is currently deactivated. Please contact your workspace administrator or our customer support team (support{\'@\'}mooncamp.com) for help.',
    },
    accountInactive: {
      heading: 'Workspace deactivated',
      explanation: 'This workspace is currently deactivated. Please contact your workspace administrator or our customer support team (support{\'@\'}mmooncamp.com) for help.',
    },
    reload: 'Reload page',
  },
  goalUpdateEditor: {
    writeComment: 'Add a comment...',
    edited: 'edited',
    goalUpdates: 'Updates',
    viewCheckIn: 'View check-in',
    deleteUpdatePrompt: 'Are you sure you want to delete this update?',
    addReaction: 'Add reaction',
    replyInThread: 'Reply in thread',
    createdAtExplanation: 'This update was posted on {date}.',
    removeAllGoalUpdates: 'Remove all',
    removeAllGoalUpdatesPrompt: 'Remove all {count} goal updates from this check-in?',
    yesRemoveAllGoalUpdates: 'Yes. Remove all',
  },
  mEditorMenuBubble: {
    turnInto: 'Turn into',
    text: 'Text',
    heading1: 'Heading 1',
    heading2: 'Heading 2',
    heading3: 'Heading 3',
    bulletedList: 'Bulleted List',
    orderedList: 'Numbered List',
    quote: 'Quote',
    code: 'Code',
    editLinkTitle: 'Edit link',
    image: 'Insert an image',
    file: 'Add a file',
  },
  networkLegend: {
    legend: 'Legend',
    belongsTo: 'Relationship',
  },
  networkControls: {
    colorByStatus: 'Color by {statusLabel}',
    progress: 'Color by progress',
  },
  propsFilter: { button: 'Properties', title: 'Properties' },
  list: {
    filter: 'Filter',
    noTitle: 'Untitled',
    ungrouped: 'Ungrouped',
    list: 'List',
    atlas: 'Network',
    cascade: 'Cascade',
    feed: 'Feed',
    tree: 'Tree',
    dashboard: 'Dashboard',
    default: 'Default',
    addTitle: 'Add a title...',
  },
  register: {
    title: 'Get started with Mooncamp',
    landingPageTitle: 'Log into workspace',
    emailNotFound: 'We could not find the email you entered. Please contact your Mooncamp admin to make sure your email address is already registered. In case you have several work emails, you may want to try alternatives.',
    accountNotFound: 'We could not find the specified workspace.',
    domainBlocked: 'Trial workspace creation is not available for this email domain. Please contact your IT department: {displayName}.',
    perk1: '14 days free trial',
    perk2: 'No credit card required',
    perk3: 'Cancel anytime',
    viaGoogle: 'Continue with Google',
    viaMicrosoft: 'Continue with Microsoft',
    viaSaml: 'Continue with SSO',
    continueWithEmail: 'Continue with email',
    continueWithCode: 'Continue with code',
    enterEmail: 'Enter your email address',
    codePlaceholder: 'Paste your code here',
    terms: 'By clicking \'Continue with email / Google / Microsoft\' you acknowledge and accept Mooncamp\'s {tos} and {privacyPolicy}.',
    tos: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    hint1: 'We just sent a confirmation code to your email.',
    hint2: 'Please check your inbox.',
    codeInvalid: 'The provided code is either invalid or expired. Please try again!',
    verifyHuman: 'Please verify you are human',
    verificationFailed: 'Verification failed',
    tryAgain: 'Try again',
  },
  login: {
    header: {
      password: 'Welcome back 👋',
      sso: 'Log in via SSO',
      ssoWithProvider: 'Log in via',
    },
    documentHeader: 'Welcome back 👋',
    callToAction: 'Log in',
    samlCallToAction: 'Continue with SSO',
    emailPasswordCombinationWrong: 'The username or password you have entered is invalid.',
    passwordLoginRestricted: 'Login via password is forbidden for this workspace. Please use SSO-Login or ask your Mooncamp admin how to log in.',
    viaGoogle: 'Log in via Google',
    viaMicrosoft: 'Log in via Microsoft',
    viaSaml: 'Log in via SSO',
    back: 'back',
    samlLoginStatus: {
      [samlLoginStatus.unableToStart]: 'Unfortunately, something went wrong. The user may not exist or SSO is not configured correctly. If you want to log in via Microsoft, go back and click the button "Log in via Microsoft".',
      [samlLoginStatus.unableToVerify]: 'Unfortunately, we couldn\'t validate the SSO request. Please check your SSO configuration.',
    },
    register: 'Sign up',
    noAccount: 'Don\'t have an account?',
    haveAccount: 'Already have an account?',
    logIn: 'Log in',
  },
  loginForm: {
    passwordForget: 'Forgot password?',
    expired: 'This link is expired',
    enterPassword: 'Enter password',
    enterEmail: 'Enter your email address...',
    notAValidEmail: 'Please enter a valid email',
    googleLoginNotAllowed: 'Log in via Google is not allowed for this workspace',
    microsoftLoginNotAllowed: 'Log in via Microsoft is not allowed for this workspace',
  },
  oauthError: {
    emailNotRegistered: {
      microsoft: '😧 We couldn\'t find a Mooncamp user for the Microsoft account {email}. Why don\'t you visit {signup} to get started with Mooncamp?',
      google: '😧 We couldn\'t find a Mooncamp user for the Google account {email}. Why don\'t you visit {signup} to get started with Mooncamp?',
    },
  },
  signIn: {
    imprint: 'Imprint',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
  },
  stepUp: {
    title: 'Confirm it\'s you 🔐',
    explanation: '{account} requires you to re-authenticate with one of the methods below before you can continue.',
    stillLocked: 'That login didn\'t satisfy the workspace\'s security requirements. Please try one of the offered methods with your own account.',
    switchInstead: 'Or continue in another workspace:',
    loggedInAs: 'Logged in as {email}',
    popupError: { [errorType.wrongIdentity]: 'You signed in as a different user. Please sign in with the account you are currently logged in with.' },
  },
  notFoundPage: {
    title: '{type} could not be found',
    description: 'The {type} was either deleted or the {type} ID entered does not exist',
    buttonText: 'Back to all {types}',
    buttonTextGoto: 'Go to {name}',
    checkInSchedule: 'Check-in schedule',
    profile: 'Profile',
    dashboard: 'Dashboard',
  },
  notFound: {
    title: '404',
    errorText: 'Error Code: 404',
    heading: 'Oops!',
    explanation: 'We can\'t seem to find the page you are looking for.',
    goBack: 'To main page',
  },
  goalChildrenList: {
    alignedItems: 'Subitems',
    adaptProps: 'Properties',
    allItemsHiddenByFilter: 'All subitems are hidden by the current filter.',
    removeFilter: 'Remove filter',
    itemsHiddenByFilter: '1 subitem is hidden by the current filter. | {count} subitems are hidden by the current filter.',
  },
  groupBy: { groupBy: 'Group by' },
  props: {
    user: {
      name: 'Name',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
    },
  },
  navItems: { noPageInside: 'No page inside' },
  profileInbox: { tasks: 'Tasks' },
  propertyOption: {
    nothingToSee: 'Nothing to see here.',
    separatorNotAllowed: 'Separator \'{separator}\' is not allowed.',
  },
  stackedBarChart: {
    total: 'Total',
    distribution: 'Share',
  },
  editGoalCycleModal: {
    createTitle: 'Create cycle',
    editTitle: 'Edit cycle',
    titleLabel: 'Name',
    titlePlaceholder: 'Enter a name...',
    rangeLabel: 'Date range',
    deletePromptLabel: 'Are you sure you want to delete the goal cycle?',
  },
  goalCycleSelector: {
    withoutCycle: 'Without cycle',
    all: 'All Cycles',
    noneSelected: 'No cycle selected',
    title: 'Select cycle',
    current: 'Active cycles',
    upcoming: 'Upcoming cycles',
    past: 'Past cycles',
    unassigned: 'Without cycle',
    more: 'more',
    selectPlaceholder: 'Select cycle...',
    searchPlaceholder: 'Search for cycle...',
    lockCycle: 'Lock cycle on view',
    tooltipLockCycleButton: 'When locked, this cycle is saved on this view and won\'t change if you select a different cycle globally.',
    tooltipLockCycleIcon: 'This cycle is locked on this view.',
    emptyGroup: 'No cycles available',
  },
  accessRightSource: {
    default: 'You have {accessRight} to this {entityType}',
    via: 'You have {accessRight} to this {entityType} via',
    accessRight: {
      full: 'full access',
      write: 'edit access',
      comment: 'comment access',
      read: 'view access',
    },
    superAccess: 'the access group "{name}"',
  },
  accessPolicy: {
    entityType: {
      default: 'resource | resources',
      goal: 'goal | goals',
      planning: 'plan | plans',
      gridPage: 'dashboard | dashboards',
      notification: 'schedule | schedules',
      updateTemplate: 'template | templates',
    },
    generalAccess: 'General access',
    accountAccessDescription: 'Everyone in this organization',
    accountAccessVia: 'via',
    accountAccessTooltip: 'This access is based on {vias}. Changing this access might restrict permissions.',
    creatorDescription: 'Creator of this goal',
    helpCenterLink: '@:{\'general.helpCenterBaseUrl\'}/access-rights',
    learnMore: 'Learn about access',
    inviteButton: 'Add people',
    copyLink: 'Copy link',
    grantAccessByUser: {
      title: 'Would you like to additionally grant access to users?',
      message: 'One or more users don\'t have access to the {entityType}. By confirming we will grant them comment access.',
    },
    warnGrantAccessByUser: {
      title: 'One or more users don\'t have access to the {entityType}',
      message: 'You do not have enough permission to grant their access.',
    },
    downgradedAccessBySpace: {
      title: 'Are you sure you want to edit the team on the {entityType}?',
      message: 'Your proposed changes would downgrade your own access to the {entityType}.',
    },
    loseFullAccess: {
      title: 'Are you sure you want to edit your own access?',
      message: 'You would lose the ability to modify access.',
    },
    disconnectingLink: {
      title: 'Change access and unlink access from team?',
      message: 'Changing this will unlink access from team access, so it will no longer inherit permission settings. Admins can still restore unlinked access.',
      cta: 'Change and unlink',
    },
    reconnectingLink: {
      title: 'Restore linked access?',
      message: 'Access will be reset to match team permissions',
      cta: 'Restore',
    },
  },
  accessPolicyForAccessGroups: {
    inviteButton: 'Add group or user',
    confirmRemoveSelfText: 'Are you sure that you want to remove yourself?',
  },
  accessPolicyScopeRow: {
    people: '1 person | {count} people',
    more: '...{count} more',
  },
  accessPolicyLinkRow: {
    title: 'People in {title}',
    permissivePatch: 'Access differs from team access',
    restrictivePatch: 'Permissions no longer follows team access',
    resetLink: 'Restore',
    owners: { title: 'Owners' },
    members: { title: 'Members' },
    mixedAccess: 'Mixed access',
  },
  accessPolicyScopeSelector: {
    buttonLabel: 'Invite',
    addMore: 'Add more',
  },
  accessPolicyTypeSelector: {
    full: 'Full access',
    write: 'Can edit',
    comment: 'Can comment',
    read: 'Can view',
    disabled: 'No access',
    remove: 'Remove',
    mixed: 'Mixed access',
    fullDescription: 'Can edit and change access.',
    writeDescription: 'Can edit, but not change access.',
    commentDescription: 'Can comment and update, but not edit.',
    readDescription: 'Cannot edit, comment, or change access.',
    title: 'Select right',
  },
  accessEditor: {
    someLoseAccess: {
      title: 'Are you sure you want to edit your own access?',
      message: 'Your proposed changes would cause you to lose the ability to see some resources.',
    },
    someLoseFullAccess: {
      title: 'Are you sure you want to edit your own access?',
      message: 'Your proposed changes would cause you to lose the ability to modify access for some resources.',
    },
    someDisconnectingLink: {
      title: 'Change access and unlink access from team?',
      message: 'Your proposed changes would cause you to unlink access from team access for some resources. Those will no longer inherit team permission settings. Admins can still restore unlinked access.',
      cta: 'Change and unlink',
    },
    nonCommon: { linkSpace: { message: 'Linked access rights cannot be adjusted in bulk' } },
  },
  updateEditor: {
    startTyping: 'Start typing...',
    writeCheckIn: 'Create check-in',
    editCheckIn: 'Edit check-in',
    viewCheckIn: 'View check-in',
    emptyTemplate: 'Empty check-in',
    placeholderHeading: 'Hit Enter to continue with an empty check-in, or choose a template.',
    addGoalsFromLastWeek: 'Add goals from your last check-in',
    addGoalsFromUserProp: 'Add goals with me as "{label}"',
    goalsWithoutCommentAccessRemoved: 'Goals for which you do not have update permissions have been removed.',
    notRecipient: "Looks like you're not a recipient of this check-in. You may be logged in as the wrong user.",
  },
  resetAccountCard: {
    heading: 'Reset workspace',
    description: 'You can clear your workspace from existing data, e.g. the demo or testing data. This is recommended before you roll out Mooncamp to your organisation. </br></br>The data cannot be restored, be careful!',
    goalLabel: 'Remove all goals from this workspace.',
    updateLabel: 'Remove all check-ins and updates from this workspace.',
    demoUserLabel: 'Remove all demo users, your own users will remain.',
    submitButton: 'Reset',
    confirmText: 'Are you sure that you want to delete the selected data? The data cannot be restored.',
  },
  deletePropertyCard: {
    heading: 'Delete Property',
    description: 'Properties are used in various cases, e.g. on goals or users, but also to define access management. If you delete a property, the data will not be recoverable. You should use this function very cautiously.',
    propertyLabel: 'Name',
    usedForLabel: 'Usage',
  },
  updateTemplate: {
    placeholder: 'Start typing...',
    prefillGoalActivities: 'Automatically add goals from check-in recipients',
    prefillGoalActivitiesHint: 'Enable this to dynamically filter and add the goals that recipients should update at the end of their check-in form.',
    where: 'Where',
    and: 'And',
    is: 'is',
    are: 'are',
    goalsUpdatedViaIntegration: 'Goals updated via integration',
    included: 'included',
    excluded: 'excluded',
    recipient: 'Recipient',
    closedGoals: 'Closed Goals',
    goals: 'Goals',
    belongTo: 'belong to any',
    cycles: 'cycles',
    currentGoalCycles: 'active',
    userSelectedGoalCycles: 'user-selected',
    currentGoalCyclesHint: 'Goals from cycles that are active at the time of check-in.',
    userSelectedGoalCyclesHint: 'Goals from the cycles the user has selected in the cycle filter.',
    pastGoalCycles: 'past',
    pastGoalCyclesHint: 'Goals from cycles that have already ended. Best combined with "Closed Goals: excluded", otherwise long-finished goals are pre-added too.',
  },
  createUpdateBtn: {
    selectTemplateTitle: 'Select schedule',
    addTemplate: 'Create template',
    deletePrompt: 'Are you sure you want to delete this template?',
    newFromScratch: 'New without schedule',
    emptyExplanation: 'No templates yet.',
  },
  updateTemplateEditor: {
    alert: 'You are editing a template for Check-ins',
    addUser: 'Add people',
    creatorDescription: 'Creator of this template',
  },
  addGoalActivityBtn: {
    addUpdate: 'Add goal updates',
    newUpdate: 'New goal updates',
    referenceUpdate: 'Link past goal updates',
    description: 'Select one or more items to update.',
    title: 'Add update',
    add: 'Add {amount} selected',
  },
  importError: {
    unknown: 'An unknown error occurred. Please try again later.',
    [importValidationFailure.usersNotUnique]: 'User is not unique in file',
    [importValidationFailure.noSuchProperty]: 'No such property',
    [importValidationFailure.noSuchPropertyOption]: 'No such property option',
    [importValidationFailure.noSuchSpace]: 'No such team',
    [importValidationFailure.noSuchUser]: 'No such user',
    [importValidationFailure.numberParse]: 'Error while reading the number',
    [importValidationFailure.timeParse]: 'Error while reading the date',
    [importValidationFailure.userExists]: 'User already exists',
    [importValidationFailure.csvMalformed]: 'The csv is malformed',
    [importValidationFailure.invalidEmail]: 'Email not valid',
    [importValidationFailure.invalidLanguage]: 'Language is not valid',
    [importValidationFailure.nameIncomplete]: 'First or last name is missing',
    [importValidationFailure.nameInvalidCharacters]: 'First or last name contains characters that are not allowed',
    [importValidationFailure.nameTooLong]: 'First or last name is too long (max 50 characters)',
    [importValidationFailure.nameExcessiveRepetition]: 'First or last name contains too many repeated characters',
    [importValidationFailure.nameContainsLink]: 'First or last name must not contain a link',
    [importValidationFailure.unauthorized]: 'User belongs to a different workspace',
    [importValidationFailure.emptyRow]: 'Row is empty',
    [importValidationFailure.userIsArchived]: 'Row best match is a deactivated user',
    [importValidationFailure.userLimitExceeded]: 'Importing would exceed the user limit for this workspace',
  },
  updatesList: {
    schedules: 'Schedules',
    explorer: 'Explorer',
    templates: 'Templates',
  },
  updateDetails: {
    update: 'Check-in',
    updates: 'Check-ins',
  },
  notificationEditorCard: {
    titleLabel: 'Name',
    recipients: 'Recipients',
    template: 'Template',
    pause: 'Pause',
    activate: 'Activate',
    deletePrompt: 'Are you sure, you want to delete this schedule?',
    notEditableNote: 'The schedule is only editable by {userName}.',
    reminders: 'Reminders',
    reminderHint: 'Reminders will only be sent to persons who did not submit their check-in yet.',
    templateIsRequired: 'Template is required',
  },
  userScopeTreeUserField: {
    sendToAll: 'Send to all users',
    sendToSegments: 'Choose specific segment',
    persons: 'person | persons',
  },
  updateTemplateSelector: {
    noTemplateSelected: 'Select a template',
    title: 'Select template',
  },
  notificationItem: { paused: 'Paused' },
  heatmap: {
    groupBy: 'Group by',
    all: 'Overall Score',
    ungrouped: 'Ungrouped questions',
    showAll: 'Show all',
  },
  searchDialog: {
    results: 'results',
    recentlyVisited: 'Recently visited',
  },
  timeRangeSelector: {
    thisMonth: 'This month',
    lastMonth: 'Last month',
    lastXMonth: 'Last {value} months',
    custom: 'Custom',
    title: 'Pick time range',
    customTitle: 'Custom',
  },
  updateFeedItem: {
    deletePrompt: 'Are you sure you want to delete your check-in?',
    edited: 'edited',
    addReaction: 'Add reaction',
    addComment: 'Comment',
    viaCheckin: 'via check-in',
  },
  reactionItem: {
    title: '{names} {have} reacted with {emoji}',
    have: 'has | have',
    you: 'You',
    have2ndFormSingular: 'have',
  },
  listFilter: {
    title: 'Filter',
    search: 'Filter by...',
    limitFilterToFirstLevel: 'Apply filter to',
    limitFilterToFirstLevelDescription: 'Choose whether the filter should apply only to the first level of the tree or to all nested levels.',
    allLevels: 'All levels',
    firstLevel: 'First level only',
  },
  listSorter: { title: 'Sort', search: 'Sort by...' },
  shareDropdown: { shareTitle: 'Share' },
  reactionList: { addEmoji: 'Add emoji' },
  commentList: {
    writeComment: 'Add a comment...',
    showMore: 'Show more comments',
    comments: 'Comment | Comments',
    latestComment: 'Last comment {latestComment}',
    viewThread: 'View comments',
  },
  commentItem: { deletePrompt: 'Are you sure you want to delete your comment?' },
  mLangPicker: { selectLanguage: 'Select language' },
  notificationFeed: {
    goalPropertyValueReferenced: '{actor} assigned you to {propertyTitle} of {goalTitle}',
    goalComment: '{actor} commented on {goalTitle}',
    goalUpdateMention: '{actor} mentioned you in {goalTitle}',
    goalUpdate: '{actor} updated {goalTitle}',
    updateMention: '{actor} mentioned you in a {checkInTitle}',
    updateComment: '{actor} commented on {checkInTitle}',
    goalUpdateComment: '{actor} commented on {goalTitle}',
    updateNotification: 'Check-In {checkinTitle}',
    dataSourceUnhealthy: 'Authorization for {dataSourceName} is about to expire',
    dataSourceDead: 'Authorization for {dataSourceName} has expired and needs to be renewed',
    dataSourceQueryError: 'Integration failing to fetch data for {goalTitle}',
    reportReady: 'Report {reportSubject} is ready',
    reply: 'Reply',
    view: 'View',
    noItems: {
      notifications: 'No updates available.',
      tasks: '🎉 No open tasks',
      all: 'No entries.',
    },
    action: {
      updateNotification: 'Perform Check-In',
      dataSourceUnhealthy: 'Renew connection',
      dataSourceDead: 'Renew connection',
      dataSourceQueryError: 'Visit goal',
      reportReady: 'Download',
    },
    actionCompleted: {
      updateNotification: 'Check-In completed',
      dataSourceUnhealthy: 'Connection renewed',
      dataSourceDead: 'Connection renewed',
      dataSourceQueryError: 'Integration restored',
    },
    tabNotifications: 'Notifications',
    tabTasks: 'Tasks',
  },
  spaceDetails: {
    owners: 'Owners',
    description: 'Description',
    addDescription: 'Add description',
    addADescription: 'Add a description...',
    deleteMessage: 'Are you sure that you want to delete "{optionTitle}"?',
  },
  savedViewSelector: {
    addView: 'Add a view',
    addViewPlaceholder: 'View name',
    selectView: 'Select view',
    createView: 'Create a view',
    editView: 'Edit view',
    duplicate: 'Duplicate',
    duplicateError: 'Failed to duplicate view. Please try again.',
    deletePrompt: 'Are you sure that you want to delete this view?',
    publish: 'Visible to all users',
    saveForEveryone: 'Save for everyone',
    canNotSaveNote: 'Your changes to this view cannot be saved for others because you don’t have sufficient permissions.',
    canNotSave: 'Changes are not saved',
    saveButtonTooltipPublic: 'Saves changes to view for everyone',
    saveButtonTooltipPrivate: 'Save changes to view',
    reset: 'Reset',
    resetFilter: 'Reset filters',
    resetSorter: 'Reset sorts',
    more: 'more...',
  },
  favoriteButton: { title: 'Favorites' },
  favoriteEditor: {
    namePlaceholder: 'Name',
    delete: 'Remove from favorites',
    create: 'Add to favorites',
  },
  mention: {
    subheading: 'Pick a person',
    noUsers: 'No users found',
  },
  feedEntryTitle: {
    checkIn: '{creator}\'s Check-In',
    genericPropertyLabel: 'a property',
  },
  goalListGroup: { disabledDragging: 'You cannot edit the order since you are missing edit rights for: {goals}' },
  createSpaceCard: {
    placeholder: 'Enter name...',
    dropdownTitle: 'Create space',
  },
  dataSources: {
    sheets: 'Google Sheets',
    asana: 'Asana',
    jira: 'Jira',
    excel: 'Excel Online',
    hubspot: 'Hubspot',
    salesforce: 'Salesforce',
    msPowerBi: 'Microsoft Power BI',
    msPlanner: 'Microsoft Planner',
    azureDevOps: 'Azure DevOps',
    errors: {
      [dataSourceQueryErrorSource.generic]: {
        [dataSourceQueryGenericError.unknown]: 'There was an error during the communication with {source}.',
        [dataSourceQueryGenericError.incompleteSetup]: 'Setup of {sourceName} is not complete. Please verify the connection to your data source.',
        [dataSourceQueryGenericError.unauthenticated]: 'Failed to authenticate. Please verify that your {sourceName} connection is still valid.',
        [dataSourceQueryGenericError.unauthorized]: 'Failed to obtain authorization. Please verify if access to {source} resources has not been revoked.',
        [dataSourceQueryGenericError.notFound]: '{source} resources could not be found. Please verify if they have not been deleted.',
        [dataSourceQueryGenericError.tooManyRequests]: 'Failed to fetch {source} resources. The target system has denied service after multiple attempts.',
      },
      [dataSourceQueryErrorSource.spreadsheet]: {
        [spreadsheetError.notANumber]: 'The selected cell does not contain a number.',
        [spreadsheetError.empty]: 'The selected cell is empty.',
        gapiError: 'There was an error while loading the Google API extension.',
      },
      [dataSourceQueryErrorSource.jira]: { [jiraError.jqlMalformed]: 'The JQL-Query has errors.' },
      [dataSourceQueryErrorSource.hubspot]: {},
      [dataSourceQueryErrorSource.asana]: {
        taskNotFound: 'The selected asana task could not be found.',
        userNotFound: 'The selected asana user could not be found.',
        sectionNotFound: 'The selected asana section could not be found.',
      },
      [dataSourceQueryErrorSource.salesforce]: {},
      [dataSourceQueryErrorSource.msPowerBi]: {
        [msPowerBiError.empty]: 'For the given configuration, no results were found.',
        [msPowerBiError.notANumber]: 'For the given configuration, the result is not a number.',
        [msPowerBiError.notASingleElement]: 'For the given configuration, the result is not a single cell.',
      },
      [dataSourceQueryErrorSource.msPlanner]: { [msPlannerError.empty]: 'For the given configuration, no task or checklist items were found.' },
      [dataSourceQueryErrorSource.azureDevOps]: {
        [azureDevOpsError.filterWorkItemTypeNotFound]: 'The selected work item type in the filter was not found. Please check your process setup in Azure DevOps.',
        [azureDevOpsError.workItemsNotFound]: 'One or more specific work items were not found. Please verify if they have not been deleted in Azure DevOps.',
        [azureDevOpsError.aggregationWorkItemTypeNotFound]: 'The selected work item type in the aggregation was not found. Please check your process setup in Azure DevOps.',
        [azureDevOpsError.fieldNotFound]: 'The aggregation field no longer exists for this type. Please check your process setup in Azure DevOps.',
      },
    },
  },
  dataSource: {
    nonSelected: 'None selected',
    untitled: 'Untitled',
    empty: 'There is no established connection yet. Create a new connection to auto-update your {title}.',
    connectToDataSource: 'Connect to data source',
    selectDataSource: 'Select data connection',
    connectedTo: 'Connected to {source}',
    syncNow: 'Sync now',
    deleteConnectionPrompt: 'Are you sure you want to delete the sync connection?',
    successfullySynced: 'Data successfully synced',
    title: 'Data source',
    alreadySet: 'The {title} is already connected to a non-shared data source. You can however choose to overwrite this data source.',
    overwriteAction: 'Overwrite data source',
    deleteAction: 'Delete connection',
    alwaysSync: 'Always sync',
    alwaysSyncHint: 'When activated, updates are also created if the value has not changed since the last update.',
  },
  jiraEditor: {
    serverLabel: 'Server',
    clientId: 'Client ID',
    clientSecret: 'Client secret',
    tokenLabel: 'Token',
    tokenHint: 'You can create API tokens from {account}, as described {description}.',
    accountHint: 'your Atlassian account',
    descriptionHint: 'here',
    authenticationType: {
      apiToken: 'Cloud',
      oauth: 'Self-managed (OAuth)',
      oauthv2: 'Self-managed (OAuth 2.0)',
    },
    signIn: 'Sign in with Jira',
    signInAgain: 'Refresh Sign-in',
    tutorial: 'Learn more about connecting to Jira',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/jira-integration',
    faultyCredentials: 'The credentials are not correct',
    emailHint: 'The email you use to sign into your jira account',
    serverHint: 'The account URL of your Jira account, for example "acme.atlassian.net"',
    serverPlaceholder: 'e.g. acme.atlassian.net',
    oauthServerPlaceholder: 'e.g. https://jira.example.com',
    oauthv2HelpLink: 'https://confluence.atlassian.com/adminjiraserver/link-to-other-applications-938846918.html',
    oauthv2Help: 'Learn more about connecting via OAuth 2.0',
  },
  dataSourceEditor: {
    connect: 'Sign in with {provider}',
    connectHint: 'Create a new connection to {provider} to automatically sync data between Mooncamp and {provider}.',
    refreshHint: 'Refresh your connection to {provider} to automatically sync data between Mooncamp and {provider}.',
    successfullyConnected: 'Successfully connected',
    shareHintDocs: 'You can share this connection. Be aware that doing so allows all members of this Mooncamp workspace to see all documents connected to the data source, even if the user doesn\'t naturally have access to the document.',
    shareHint: 'You can share this connection. Be aware that doing so allows all members of this Mooncamp workspace to access the available metrics.',
    nameLabel: 'Connection name',
    sharedLabel: 'Share connection with all users',
  },
  dataSourceItem: {
    deletePrompt: 'Are you sure you want to delete this data connection? There might be {title}, which use this connection and will consequently no longer be synchronized.',
    editTitle: 'Connect to {source}',
    refreshAction: 'Refresh connection',
    refreshDescription: 'Expired connections require renewed consent. Click the button to refresh the connection.',
    connectionUnhealthy: 'Connection expiring',
    connectionUnhealthyDescription: 'The connection is about to expire. Please contact the creator of the connection and request it to be renewed.',
    connectionExpired: 'Connection expired',
    connectionExpiredDescription: 'The connection has expired. Please contact the creator of the connection and request it to be renewed.',
    establishAction: 'Complete connection',
    incompleteDescription: 'The connection has not been established yet.',
    creatorDescription: 'Created by: {creatorName}',
  },
  spreadsheetForm: {
    help: 'Learn about the Google Sheets / Excel Online integration.',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/google-sheets-integration',
    selectDocumentLabel: 'Document',
    selectSheetLabel: 'Sheet',
    selectColumnLabel: 'Column',
    selectRowLabel: 'Row',
    viewFile: 'View file',
    error: {
      excel: 'There was an error during the communication with Excel Online. Please try connecting using a different data source.',
      sheets: 'There was an error during the communication with Google Sheets. Please try connecting using a different data source.',
    },
    selectFile: 'Select document',
    changeFile: 'Change selection',
    excelHint: 'Data successfully synced. Be aware, that changes in Excel sometimes take a few seconds until they are available to Mooncamp.',
  },
  jiraForm: {
    help: 'Learn about the Jira integration.',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/jira-integration',
    onlyDoneLabel: 'Count only done tickets',
    jqlLabel: 'JQL',
    trackMetricLabel: 'Track Progress by',
    openInJira: 'Open in Jira',
    operationModes: {
      overallProgress: {
        label: 'Overall progress',
        description: 'Measure overall progress (%) of certain issues vs. total issues as specified below.',
      },
      countOfIssues: { label: 'Count of issues', description: 'Returns the count of the issues specified below.' },
    },
    learnJql: 'Learn more about JQL',
    learnJqlLink: 'https://www.atlassian.com/software/jira/guides/expand-jira/jql',
  },
  jiraJqlEditor: { placeholder: 'Insert JQL query...' },
  jiraCountOfIssues: { label: 'JQL query' },
  jiraOverallProgress: {
    countIssues: 'Count of issues',
    totalIssues: 'Total issues',
  },
  msPowerBIForm: {
    help: 'Learn about the Microsoft Power BI integration.',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/microsoft-power-bi-integration',
    selfGroup: 'My Workspace',
    selectGroupLabel: 'Workspace',
    selectDatasetLabel: 'Dataset',
    daxQueryLabel: 'DAX Query',
    daxQueryPlaceholder: 'EVALUATE ...',
  },
  msPlanner: {
    aggregationMode: {
      absolute: {
        task: {
          label: 'Count of completed tasks',
          description: 'Use this option to count the number of tasks marked as Completed in Planner.',
        },
        checklist: {
          label: 'Count of completed checklist items',
          description: 'Use this option to count the number of checklist items marked as Completed in Planner.',
        },
      },
      percentage: {
        task: {
          label: 'Progress of tasks',
          description: 'Use this option to track the progress of your Planner tasks. Completed tasks count as 100%, while tasks "In Progress" count as 50%. The overall score is calculated as the relative progress of all selected tasks.',
        },
        checklist: {
          label: 'Progress of checklist items',
          description: 'Use this option to track the progress of your Planner\'s checklist items. The overall score is calculated as the percentage of completed items out of all selected items.',
        },
      },
    },
    filterMode: {
      filter: { label: 'Filters', description: 'Tracks a group of tasks filtered by buckets or labels.' },
      tasks: { label: 'Distinct tasks', description: 'Track the specific set of tasks.' },
    },
  },
  msPlannerForm: {
    help: 'Learn about the Microsoft Planner integration.',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/microsoft-planner-integration',
    aggregationLabel: 'How do you want to track progress?',
    selectPlanLabel: 'Plan',
    selectBucketsLabel: 'Buckets (optional)',
    selectLabelsLabel: 'Labels (optional)',
    selectTasksLabel: 'Tasks (optional)',
    labels: {
      category1: 'Pink',
      category2: 'Green',
      category3: 'Aqua',
      category4: 'Red',
      category5: 'Yellow',
      category6: 'Blue',
      category7: 'Purple',
      category8: 'Bronze',
      category9: 'Lime',
      category10: 'Grey',
      category11: 'Silver',
      category12: 'Brown',
      category13: 'Cranberry',
      category14: 'Orange',
      category15: 'Peach',
      category16: 'Marigold',
      category17: 'Light green',
      category18: 'Dark green',
      category19: 'Teal',
      category20: 'Light blue',
      category21: 'Dark blue',
      category22: 'Lavender',
      category23: 'Plum',
      category24: 'Light grey',
      category25: 'Dark grey',
    },
  },
  azureDevOps: {
    filterMode: {
      sharedQueries: {
        label: 'Shared query',
        shortDescription: 'Shared query',
        description: 'Track work items from an existing shared query from Azure DevOps.',
      },
      workItems: {
        label: 'Work items',
        shortDescription: 'Work items and their children',
        description: 'Track a specific set of work items and all their children.',
      },
    },
    aggregationMode: {
      absolute: {
        label: 'Absolute',
        fields: { System_Id: 'Work Items' },
        info: 'Track progress by counting work items or summing their field values (e.g., story points). For example, 5 items worth 5 story points each = 25 SPs.',
        shortDescription: {
          System_Id: 'Count of work items',
          Microsoft_VSTS_Scheduling_StoryPoints: 'Sum of story points',
          Microsoft_VSTS_Scheduling_Effort: 'Sum of effort',
          Microsoft_VSTS_Scheduling_OriginalEstimate: 'Sum of original estimate hours',
          Microsoft_VSTS_Scheduling_RemainingWork: 'Total remaining work hours',
          Microsoft_VSTS_Scheduling_CompletedWork: 'Total completed work hours',
        },
        description: {
          System_Id: 'Number of work items for the given query or work item type.',
          Microsoft_VSTS_Scheduling_StoryPoints: 'Sum of story points for the work items for given query or work item type.',
          Microsoft_VSTS_Scheduling_Effort: 'Sum of effort for the work items for given query or work item type.',
          Microsoft_VSTS_Scheduling_OriginalEstimate: 'Sum of original estimate hours for the work items for given query or work item type.',
          Microsoft_VSTS_Scheduling_RemainingWork: 'Total remaining work hours for the work items for given query or work item type.',
          Microsoft_VSTS_Scheduling_CompletedWork: 'Total completed work hours for the work items for given query or work item type.',
        },
      },
      percentage: {
        label: 'Percentage',
        fields: { System_Id: 'Completed Work Items' },
        info: 'Track by percentage, ranging from 0% to 100%.',
        shortDescription: {
          System_Id: 'Percent of completed work items',
          Microsoft_VSTS_Scheduling_StoryPoints: 'Percent of story points for the completed work items',
          Microsoft_VSTS_Scheduling_Effort: 'Percent of effort for the completed work items',
          Microsoft_VSTS_Scheduling_OriginalEstimate: 'Percent of total original estimate hours for the completed work items',
          Microsoft_VSTS_Scheduling_RemainingWork: 'N/A',
          Microsoft_VSTS_Scheduling_CompletedWork: 'Percent of total completed work hours for the completed work items',
        },
        description: {
          System_Id: 'Percent of completed work items out of the total work items for the given query or work item type.',
          Microsoft_VSTS_Scheduling_StoryPoints: 'Percent of story points for the completed work items out of the total story points for the work items for given query or work item type.',
          Microsoft_VSTS_Scheduling_Effort: 'Percent of effort for the completed work items out of the total effort for the work items for given query or work item type.',
          Microsoft_VSTS_Scheduling_OriginalEstimate: 'Percent of total original estimate hours for the completed work items out of the total original estimate hours for all the work items for given query or work item type.',
          Microsoft_VSTS_Scheduling_RemainingWork: 'N/A',
          Microsoft_VSTS_Scheduling_CompletedWork: 'Percent of total completed work hours for the completed work items out of the total completed work hours for all work items for the given query or work item type.',
        },
      },
    },
  },
  azureDevOpsForm: {
    help: 'Learn about the Azure Dev Ops integration.',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/azure-devops-integration',
    selectOrganizationLabel: 'Organization',
    selectProjectLabel: 'Project',
    filter: 'Filter work items',
    filterInfo: 'Define which work items from Azure DevOps you want to track. You can either select a shared query with a predefined set or tree of work items – or filter by work item types or specific items of a given type.',
    filterModeLabel: 'Choose filter method',
    selectSharedQueryLabel: 'Shared query',
    sharedQueryWorkItemsHint: '{count} matching working items',
    selectFilterWorkItemTypeLabel: 'Select parent work item type',
    selectFilterWorkItemTypeTooltip: 'Select the main work item type. Work items of this type and their children will be included for tracking.',
    selectWorkItemsLabel: 'Select parent work items',
    selectWorkItemsTooltip: 'Select specific parent work items to track. Only these work items and their children will be included in tracking.',
    aggregation: 'Measurement',
    aggregationInfo: 'Track progress by absolute (e.g. count of work items) or by percentage (e.g., story points from completed work items out of all work items)',
    selectAggregationWorkItemTypeLabel: 'Restrict work items by type (optional)',
    selectAggregationWorkItemTypeTooltip: 'You can filter the tree of work items further by type (e.g., Task). If you only want to track a certain type, select it here.',
    selectAggregationWorkItemFieldLabel: 'Choose tracking method',
    aggregationWorkItemFieldPlaceholder: 'Select Azure DevOps field...',
  },
  goalProgress: {
    dataConnectionTitle: 'Data connection',
    autoUpdateDescription: 'Auto-updated through {dataSourceType}',
  },
  dataSourceSummary: {
    connectedBy: 'Connected by',
    private: 'Private data source',
    syncError: 'Connection error',
  },
  googleSheetsSummary: {
    column: 'Column',
    row: 'Row',
    sheetName: 'Sheet',
  },
  jiraSummary: {
    openInJira: 'Open in Jira',
    operationMode: 'Calculation Mode',
    empty: 'any Jira Issue',
  },
  hubspotSummary: {
    trackMetric: 'Track Metric',
    startDate: 'Start',
    endDate: 'End',
  },
  asanaSummary: {
    project: 'Project',
    assignedTo: 'Assigned To',
    sections: 'Task Status',
    task: 'Task',
    operationMode: 'Calculation Mode',
  },
  salesforceSummary: {
    report: 'Report',
    field: 'Field',
  },
  msPowerBiSummary: {
    group: 'Workspace',
    dataset: 'Dataset',
    dax: 'DAX Query',
  },
  msPlannerSummary: {
    aggregation: 'Aggregation',
    plan: 'Plan',
    buckets: 'Bucket | Buckets',
    labels: 'Label | Labels',
    tasks: 'Task | Tasks',
  },
  azureDevOpsSummary: {
    organization: 'Organization',
    project: 'Project',
    filter: 'Filters',
    sharedQueries: 'by query',
    filterWorkItemType: 'by type',
    filterWorkItems: 'by item | by items',
    aggregation: 'Aggregation',
    aggregationWorkItemType: 'for type',
  },
  dataSourceQueryError: { details: 'Details' },
  dataSourcePicker: {
    title: 'Data sources',
    integrations: 'Integrations',
    defaultName: '{source} Data Source',
  },
  salesforce: {
    selectReportLabel: 'Report',
    selectFieldLabel: 'Field',
  },
  asanaForm: {
    help: 'Learn about the Asana integration.',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/asana-integration',
    selectProjectLabel: 'Project',
    selectWorkspaceLabel: 'Workspace',
    selectAssignedTo: 'Assigned to (optional)',
    selectSection: 'Progress measured by',
    sectionOrStatus: 'Section or status (optional)',
    completed: 'Completed',
    countMeasuredBy: 'Count measured by',
    completionMeasuredBy: 'Completed if',
    singleTask: 'Connect single task',
    operationModes: {
      overallProgress: {
        label: 'Overall progress',
        description: 'Measure overall progress (%) of certain tasks vs. total tasks as specified below.',
      },
      countOfTasks: { label: 'Count of tasks', description: 'Returns the count of the tasks specified below.' },
      singleTask: { label: 'Single task', description: 'Track the completion of a single task.' },
    },
    overallLabel: 'Total tasks as measured by',
    emptyPlaceholder: 'Any',
    selectTask: 'Select a task',
    task: 'Task',
    all: 'All',
    and: 'And',
    asanaCompletionStatus: {
      completed: 'Completed',
      incomplete: 'Incomplete',
      any: 'Any',
    },
    countTasks: 'Selected tasks',
    countTasksHint: 'Number of tasks that determine progress.',
    totalTasks: 'Total tasks',
  },
  asanaSectionForm: {
    section: 'Section',
    status: 'Status',
    or: 'Or',
    addAction: 'Add Section',
    any: 'Any',
  },
  asanaTaskSelectionTrigger: {
    assignedTo: 'Assigned to',
    allTasks: 'All tasks',
    any: 'Any',
    criteriaSelected: 'criteria selected',
  },
  hubspotForm: {
    help: 'Learn about the Hubspot integration.',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/hubspot-integration',
    trackMetric: {
      numberOfRawViews: 'Number of Raw Views',
      numberOfVisits: 'Number of Visits',
      numberOfVisitors: 'Number of Visitors',
      numberOfLeads: 'Number of Leads',
      numberOfContacts: 'Number of Contacts',
      numberOfSubscribers: 'Number of Subscribers',
      numberOfMqls: 'Number of MQLs',
      numberOfSqls: 'Number of SQLs',
      numberOfOpportunities: 'Number of Opportunities',
      numberOfCustomers: 'Number of Customers',
      averagePageViewsPerSession: 'Average Page Views per Session',
      bounceRate: '% Bounce Rate',
      timePerSession: 'Time Per Session (in Seconds)',
      newSessions: '% New Sessions',
      sessionToContact: '% Session to Contact',
      contactToCustomer: '% Contact to Customer',
    },
    selectTrackMetric: 'Track Metric',
    selectTimeRange: 'Select Time Range (optional)',
    startPlaceholder: 'Start',
    endPlaceholder: 'End',
  },
  onboarding: {
    personalInfosTitle: 'Welcome to Mooncamp',
    personalInfosSubTitle: 'Please tell us a bit about yourself.',
    accountInfosTitle: 'Create your company workspace',
    accountInfosSubTitle: 'Please fill in some details about your company.',
    moduleSelectionTitle: 'Which tools are you planning to use?',
    moduleSelectionSubTitle: 'We’ll adjust your onboarding guide accordingly.',
    continue: 'Continue',
    loggedInAs: 'You\'re currently logged in as {email}. If you don\'t intend to set up a new workspace, you can {link}',
    logInAsDifferentUser: 'log in as a different user',
    launchMooncamp: 'Launch Mooncamp',
  },
  personalInfos: {
    uploadImage: 'Add a photo',
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
    passwordLabel: 'Password',
  },
  accountInfos: {
    uploadImage: 'Add a logo',
    companyNameLabel: 'Company name',
    companySize: 'Number of employees',
  },
  nameValidation: {
    personNameEmpty: 'Name cannot be empty.',
    personNameTooLong: 'Name cannot exceed {max} characters.',
    personNameInvalidChars: 'Name can only contain letters, spaces, hyphens, apostrophes, and periods.',
    companyNameEmpty: 'Company name cannot be empty.',
    companyNameTooLong: 'Company name cannot exceed {max} characters.',
    companyNameInvalidChars: 'Company name can only contain letters, numbers, spaces, and common punctuation.',
    excessiveRepetition: 'Must not contain 4 or more repeated characters in a row.',
    containsLink: 'Must not contain links.',
  },
  moduleSelectionInfos: {
    okrTitle: 'OKRs',
    okrDescription: 'Create alignment, transparency and focus.',
    creatingYourAccount: 'Creating your workspace...',
  },
  msTeamsTeamDetails: {
    hint: 'You are currently not in any team.',
    employeePage: 'You can change your team settings on the {employeePage}.',
    employeePageLinkDesc: 'Employee Page',
    adminRequest: 'Please ask a Mooncamp Admin to assign you to your team.',
    myTeams: 'Teams',
  },
  msTeamsAccountDetails: {
    unableToFindAccountSpace: 'We were unable to find the workspace.',
    goToBtn: 'Go to Team OKRs',
  },
  msTeams: {
    connectionEstablished: 'Connection to the Teams-Bot established.',
    connectionError: 'Error connecting to the Teams-Bot.',
    notEnabled: "The Microsoft Teams integration isn't enabled for your workspace. Activate it in your workspace settings, or ask an admin to enable it, then try connecting again.",
    activate: 'Activate',
  },
  msTeamsConnect: {
    title: 'Connect Mooncamp to Microsoft Teams',
    body: 'Choose which workspace to connect. The Mooncamp bot will act in this workspace in Teams. You can switch later with the "switch workspace" command in Teams.',
    allow: 'Allow access',
    cancel: 'Cancel',
    missingActivity: 'This connect link is invalid or has expired. Please start again from the Teams bot.',
    permissionsHeading: 'By connecting, you allow the Mooncamp Teams bot to:',
    permissions: {
      readGoals: 'View your goals and their progress',
      updateGoals: 'Update your goals and check-ins',
      notifications: 'Send you notifications and reminders',
    },
    success: 'Connection to the Teams bot established.',
  },
  onboardingGuide: {
    inviteUserModal: 'Invite members',

    gettingStarted: {
      title: 'Getting Started',
      introVideo: { title: 'Mooncamp 101 video' },
      appTour: { title: 'Take a quick app tour' },
      whyMooncampIsUnique: { title: 'Tips & tricks in Mooncamp' },
      videoTutorials: { title: 'Watch our video tutorials' },
      okrResources: { title: 'Check out our OKR resources' },
    },
    accountSetupNew: {
      title: 'Workspace Setup',
      removeDemoData: { title: 'Remove demo data' },
      messengers: { title: 'Connect to Slack or MS Teams' },
      inviteTeamMembers: { title: 'Invite your team members' },
      checkins: { title: 'Set up check-ins for your teams' },
    },
    markAllAsDone: 'Mark all as done',
  },
  inviteUsers: {
    description: 'Invite people to join your workspace.',
    descriptionInvite: 'They will receive an invitation email.',
    inviteUsersLabel: 'Emails',
    inviteUsersPlaceholder: 'Enter email address',
    addEmail: 'Add',
    invalidEmail: 'Invalid email',
    alreadyAdded: 'Email already added',
    spaceLabel: 'Add people to team (optional)',
    spacePlaceholder: 'No team selected',
    languageLabel: 'Language for invited people',
    languageHint: 'Sets the language of the invitation email and their initial experience. People who already have a Mooncamp account keep their own language.',
    accessGroupLabel: 'Add to access group (optional)',
    accessGroupPlaceholder: 'No access group selected',
    accessGroupAssignFailed: 'Users were invited, but could not be added to the access group.',
    sendInviteEmailLabel: 'Send invitation email',
    sendInviteEmailHint: 'Turn off to add users without notifying them. You can send the invites later in bulk from the user list.',
    sendInvite: 'Send invites',
    addUsers: 'Add users',
    oneEmailAlreadyExists: 'One or more of the entered email addresses are already members of this workspace.',
    emailAlreadyInAccount: 'This email address is already a member of this workspace',
    success: 'Invites successfully sent',
    usersAddedSuccess: 'Users successfully added',
  },
  trialBox: {
    // 0,1,n
    expired: 'Your trial period has expired.',
    today: 'Your trial ends today!',
    tomorrow: 'Your trial ends tomorrow!',
    remaining: 'There are {amount} remaining in your free trial.',
    days: '{amount} days',
    button: 'Upgrade',
    bookDemo: 'Book a demo',
    demoLink: '@:{\'general.websiteBaseUrl\'}/contact/',
  },
  subscriptionInfo: {
    heading: 'Start subscription',
    headingExpired: 'Looks like your trial period has expired 😱',
    text1: 'If you want to subscribe to one of our plans, extend your trial period or have any question regarding Mooncamp\'s software, please contact us at',
    text2: 'The Mooncamp Team',
  },
  notificationComment: { reply: 'Reply' },
  goalSettings: {
    title: 'Goal Settings',
    advanced: {
      title: 'Advanced settings',
      multiGoalAlignment: {
        title: 'Multi-alignment of goals',
        subTitle: 'Align a goal with two or more parent goals.',
      },
      publishedViews: {
        title: 'Public links to views',
        subTitle: 'Create public views that are visible to everyone.',
      },
      weightedGoals: {
        title: 'Weighted goals',
        subTitle: 'Give weight to goals to reflect importance in progress calculation',
      },
      canGroupByUser: {
        title: 'Employee grouping for Insights',
        subTitle: 'Enable or disable individual user breakdowns (e.g., Progress Dashboard)',
      },
    },
    accessRight: {
      title: 'Default access',
      description: 'You can set a default access rule that is applied when users create new goals.',
      addUserText: 'Add group or user',
      exampleUser: {
        firstName: 'Example',
        lastName: 'User',
      },
    },
    insightRanges: {
      title: 'Progress Indicator Thresholds',
      description: 'Adjust the thresholds to define what constitutes good, medium, and bad progress on your OKRs. These settings will influence how progress is visually represented on the progress dashboard and the goal network view.',
    },
    featureName: {
      title: 'Display name',
      subTitle: 'If your organization uses a different term for goals, you can change it here',
      defaultSingular: 'Goal',
      defaultPlural: 'Goals',
      labelSingular: 'Singular',
      labelPlural: 'Plural',
    },
  },
  spaceNavMenu: {
    createTitle: 'Create {title}',
    editTitle: 'Edit {title}',
    createSubspace: 'Create subgroup',
    move: 'Move',
    pin: 'Pin to sidebar',
    unpin: 'Unpin from sidebar',
    settings: '{title} settings',
  },
  moveSpaceCard: {
    dropdownTitle: 'Move group',
    move: 'Move',
    placeholder: 'No parent selected',
  },
  goalInsights: {
    user: 'Employee',
    groupBy: 'Group by',
  },
  goalInsightsTable: {
    user: 'Employee',
    publicGoals: 'Public {title}',
    lastUpdate: 'Last update',
    goalsByStatus: '{title} by {statusType}',
    progress: 'Progress',
    confidence: 'Avg. score',
    confidenceDistribution: '{title} distribution',
    progressExpectedValueAbbreviation: 'EV',
    progressExpectedValueTooltip: 'Expected value',
    subOfSubGoals: 'Sum of direct goals and subgoals',
  },
  goalInsightsPerformance: {
    statusActive: 'Active',
    statusClosed: 'Closed',
    overview: 'Overview',
    overallProgress: 'Overall progress',
    averageScore: 'Average score',
    goalsAtRisk: '{title} at risk',
    totalNumberOfGoals: 'Total number of {title}',
    goalsTotal: 'Total:',
    goalsByType: '{title} by type',
    goalsByProgress: '{title} by progress: {label}',
    goalsByStatus: '{title} by {statusLabel}: {label}',
    noGoalType: 'No type',
    progress: 'Progress',
    progressChart: 'Average progress',
    progressDistribution: 'Progress distribution',
    statusChart: '{title} over time',
    statusDistribution: '{title} distribution',
    confidenceChart: '{title} over time',
    confidenceChartHint: 'In the progress dashboard chart, your customized progress thresholds (ranging from 0% to 100%) are condensed into a simplified scale from 0 to 10.',
    breakdown: 'Breakdown',
    breakdownTable: '{title} progress grouped by {groupBy}',
  },
  goalInsightsBoundedProgressMenu: {
    optionLabel: 'Average progress',
    bounded: 'Cap at 100%',
    unbounded: 'No cap',
    boundedTooltip: 'Caps each goal\'s progress at 100% before averaging.',
    unboundedTooltip: 'Includes progress above 100%, which can pull the average higher.',
  },
  goalInsightsHealth: {
    goToSettings: 'Go to settings',
    infobox: {
      text: 'The Health dashboard is based on the rules defined in the settings area under “Types and rules”. The dashboard allows you to quickly identify and solve any issues with your goals, like missing property values, invalid parent goals, or measurement types.',
      learnMore: 'Learn more',
      helpCenterLink: '@:{\'general.helpCenterBaseUrl\'}/health-dashboard',
    },
    groups: {
      overview: 'Overview',
      byGoalType: 'Health check by goal type',
      breakdown: 'Breakdown',
    },
    overview: {
      parentType: 'Goals with invalid parent goal',
      propertiesRequired: 'Goals with missing property values',
      measurementTypes: 'Goals with invalid measurement types',
    },
    measurementTypes: {
      cardTitle: 'Goals with invalid vs. valid measurement types',
      cardHelp: 'This report is based on the allowed measurement types which can be configured under {settingsTitle}.',
      dialogTitle: '{goalType} - Measurement type - {label}',
    },
    automated: {
      cardTitle: 'Automated vs. manual',
      cardHelp: 'Distribution of goals that are updated manually vs. automatically via data integrations.',
      labelAutomated: 'Automated',
      labelManual: 'Manual',
      dialogTitle: '{goalType} - Updates - {label}',
    },
    parentType: {
      cardTitle: 'Goals with invalid vs. valid parent goal',
      cardHelp: 'This report is based on the rules defined for the parent alignment property which can be configured under {settingsTitle}.',
      labelWrong: 'Invalid parent goal',
      labelRight: 'Valid parent goal',
      dialogTitle: '{goalType} - Alignment - {label}',
    },
    propertiesRequired: {
      cardTitle: 'Goals with missing vs. existing property values',
      cardHelp: 'This report shows the number of goals with missing vs. existing property values. The report is based on the properties that are marked as required. They can be configured under {settingsTitle}.',
      labelWrong: 'Missing',
      labelRight: 'Existing',
      dialogTitle: '{goalType} - Property values - {label}',
    },
    breakdown: {
      cardTitle: 'Health check grouped by {groupBy}',
      table: {
        publicGoals: 'Number of goals',
        invalidParents: 'Invalid parent goal',
        invalidProperties: 'Missing property values',
        invalidMeasurementTypes: 'Invalid measurement types',
      },
    },
  },
  singleMetricCard: {
    expectedValue: 'expected value: {expectedValue}',
    expectedValueRange: 'Values between {from} and {to}',
  },
  accessGroupForm: {
    defaultGroupTitle: 'Member',
    defaultGroupDescription: 'This access group includes all users of this workspace.',
    accountTitle: 'Workspace administration',
    memberTitle: 'Group members',
    accessGroupsTitle: 'Access rights',
    goalsTitle: 'Goals',
    updatesTitle: 'Check-ins',
    usersTitle: 'Users',
    groupNameLabel: 'Name',
    accountWriteAccess: 'Management of  workspace settings (Admin)',
    accessGroupWriteAccessLabel: 'Management of access groups (Admin)',
    userWriteAccessLabel: 'Management of users',
    propertyWriteAccessLabel: 'Management of properties',
    spaceWriteAccessLabel: 'Management of teams',
    publicSavedViewAccessLabel: 'Management of public views',
    referenceForeignEntityLabel: {
      tooltip: 'For team on goals, allow users to choose from all workspace teams (bypassing teams\' permissions)',
      label: 'Can choose from all teams',
    },
    createNewAccessGroup: 'Create new access group',
    updateAccessGroupTitle: 'Edit access group',
    accessGroupsHeading: 'Access rights',
    userHeading: 'Users',
    selectUserHeading: 'Select users',
    userLabel: 'Select users',
    deletePrompt: 'Are you sure that you want to delete this access group?',
    goalCycleWriteAccessLabel: 'Management of goal settings and cycles',
    createGoalAccessLabel: 'Can create goals',
    goalSuperAccessLabel: 'Unrestricted access to all goals',
    goalDashboardViewAccessLabel: 'Can view progress and health dashboards',
    adminWarning: 'The admin group needs at least one group member.',
    updateAdminAccess: 'Management of check-in templates and settings',
    writeDashboardsAndReports: 'Can create dashboards and reports',
    dashboardSuperAccess: 'Unrestricted access to all dashboards',
    checkinScheduleSuperAccess: 'Unrestricted access to all check-in schedules',
  },
  accessGroupList: {
    nameLabel: 'Name',
    numberOfMembersLabel: 'Group members',
    actions: 'Actions',
    editGroup: 'Edit group',
  },
  breadcrumbs: { items: 'Items' },
  contextMenu: { editProperty: 'Edit property' },
  goalsContextMenu: {
    selected: 'selected',
    selectWithSubgoals: 'Expand and select',
    deleteHint: 'For some {title} you have no permissions to edit them.',
    cannotDeleteMultiple: 'You don\'t have the rights to edit this {title}. | You don\'t have the rights to edit these {title}.',
    addBeneath: 'Create subgoal',
    duplicateToCycle: 'Duplicate to cycle',
    duplicateToCycleHint: 'You are not allowed to duplicate to cycle {title} in draft.',
    changeAccess: 'Edit access rights',
    changeAccessHint: 'You are not allowed to edit the access rights of all selected {title}.',
    addToDashboard: 'Add to dashboard',
    addToDashboardHint: 'Not all selected {title} are allowed on dashboards.',
    cannotAddToDashboard: 'Only {title} with continuous progress measurement can be added to a dashboard.',
    disableStatusAutoUpdate: 'Automated status update',
    statusUpdateTooltip: 'Automatically update the status based on this goal’s subitems.',
    more: 'More',
  },
  goalPicker: {
    title: 'Pick a {title}',
    selected: 'selected',
  },
  goalActivityPicker: { title: 'Pick updates' },
  compareToPrevious: {
    title: 'Compare to',
    customDate: 'Custom date',
    pickADate: 'Pick a date',
    oneDayAgo: 'One day ago',
    oneWeekAgo: 'One week ago',
    oneMonthAgo: 'One month ago',
    none: 'None',
  },
  colorPicker: {
    title: 'Pick a color',
    default: 'Light Grey',
    grey: 'Grey',
    brown: 'Brown',
    orange: 'Orange',
    yellow: 'Yellow',
    green: 'Green',
    blue: 'Blue',
    purple: 'Purple',
    pink: 'Pink',
    red: 'Red',
  },
  savedViewInfo: {
    filterLabel: 'Filters for',
    propertyLabel: 'Properties for',
    sortLabel: 'Sort for',
    privateTooltip: 'This is a private view that is only visible to you.',
    disabledInfo: 'You don\'t have sufficient rights to edit public views.',
  },
  goalsCascadeTable: {
    emptyGoalList: 'No {title} available',
    addTooltip: 'Click to create a subgoal',
    moreTooltip: 'Click to see actions',
  },
  editorImage: { original: 'Show original', addAnImage: 'Select an image' },
  editorFile: { addAFile: 'Select a file' },
  fileUpload: {
    fileTooBig: 'File is too big (max. 20 MB)',
    featureDisabled: 'File upload is disabled for this workspace.',
  },
  samlChooseAccount: {
    title: 'Choose a workspace',
    body: 'The email {email} belongs to multiple workspaces with single sign-on. Choose the workspace you want to log in to.',
    invalid: 'This login link is invalid or has expired. Please start again from the login page.',
    backToLogin: 'Back to login',
  },
  slackConnect: {
    title: 'Connect Mooncamp to Slack',
    body: 'Choose which workspace to connect. The Mooncamp bot will act in this workspace for your Slack workspace. You can switch later with the "switch workspace" command in Slack.',
    allow: 'Allow access',
    cancel: 'Cancel',
    missingRequest: 'This connect link is invalid or has expired. Please start again from Slack.',
    permissionsHeading: 'By connecting, you allow the Mooncamp Slack bot to:',
    permissions: {
      readGoals: 'View your goals and their progress',
      updateGoals: 'Update your goals and check-ins',
      notifications: 'Send you notifications and reminders',
    },
  },
  profileAccess: {
    title: 'Access',
    password: {
      sectionHeading: 'Password',
      heading: 'Set a password',
      subHeading: 'Sends you an email with a one-time link to set a new password.',
      button: 'Set a password',
      promptTitle: 'Set a new password',
      content: 'We will send an email to "{email}", that will allow you to set a new password.',
      okText: 'Send email now',
      success: 'Help is on the way! Have a look in your email inbox',
    },
  },
  personalAccessTokens: {
    title: 'Personal Access Tokens',
    sectionDescription: 'Personal access tokens authenticate API requests on your behalf. Treat them like passwords. Tokens expire after at most {days} days.',
    name: 'Name',
    expiresAt: 'Expires',
    lastUsedAt: 'Last used',
    rotatedAt: 'Rotated',
    neverUsed: 'Never',
    expired: 'Expired',
    create: 'Create token',
    createTitle: 'Create personal access token',
    namePlaceholder: 'What is this token for?',
    expiresAtLabel: 'Expiration date',
    expiresAtHint: 'Your workspace allows a maximum lifetime of {days} days.',
    createdTitle: 'Token details for "{name}"',
    createdHint: 'Make sure you copy your token now. You will not be able to access it again.',
    rotate: 'Rotate',
    rotateTitle: 'Rotate token',
    rotateHint: 'Rotating "{name}" creates a new secret and invalidates the current one immediately.',
    revoke: 'Revoke',
    revokePrompt: 'Revoke "{name}"? Any integration using it will stop working immediately.',
    maxLifetimeTitle: 'Maximum token lifetime',
    maxLifetimeDescription: 'Applies to new and rotated tokens of all members.',
    days: '{days} days',
  },
  myNotifications: {
    title: 'Notifications',
    channelHeading: 'Channels',
    msTeamsHint: 'Receive notifications via Microsoft Teams.',
    msTeamsDisabledHint: 'Microsoft Teams must be enabled for this workspace first, in the integration settings.',
    slackHint: 'Receive notifications via Slack.',
    emailHint: 'Receive notifications via email.',
    emailTitle: 'Email',
    notificationTypes: 'Messages',
    directMessageTitle: 'Direct messages',
    directMessageSubTitle: 'Get a notification when someone replies to your comments or updates, or when you are mentioned with {\'@\'}username.',
    checkinReminderTitle: 'Check-in reminders',
    checkinReminderSubTitle: 'Get a notification when it is time to make a check-in.',
    goalSubscriptionTitle: 'Subscribed goals',
    goalSubscriptionSubTitle: 'Get a notification when there are new updates for your subscribed goals.',
    goalReferenceTitle: 'Mention in a goal',
    goalReferenceSubTitle: 'Get a notification when you are mentioned in a goal.',
    reportTitle: 'Reports',
    reportSubTitle: 'Get a notification when reports are ready for you.',
    disconnect: 'Disconnect',
    confirmMicrosoftDisconnect: 'Do you really want to disconnect Mooncamp from Microsoft Teams?',
  },
  checkinOnboardingModal: {
    onboardingTitle: 'What are check-ins?',
    onboardingDescription: 'A regular check-in is the most effective factor of every OKR process. It is a simple tool to keep OKRs top of mind, let everyone see what others are working on, discuss progress and new insights and to uncover where mutual support is needed. It ensures continuous feedback and learning as well as a high level of participation and responsibility in teams.',
    createCheckin: 'Take a quick tour',
    explore: 'Explore on your own',
  },
  demoDataModal: {
    title: 'Welcome to Mooncamp 👋',
    description: 'Get to know Mooncamp in just <b>5 minutes</b>:',
    takeTour: 'Take a quick tour',
    skipTour: 'Skip tour',
    introVideo: 'https://www.loom.com/embed/05d0d570bca4465e82d711f7e2ac4ca6',
  },
  exportButton: {
    buttonText: 'Export CSV',
    exportingCSV: 'Exporting goals...',
    tooManyRequests: 'Only one csv export operation is allowed per minute. Try again later.',
  },
  exportMenu: {
    export: 'Export',
    exportFormat: 'Export format',
    selectExportFormat: 'Select export format',
    printableLimitHint: 'PDF and image exports are limited to {limit} goals. Use CSV to export your full selection.',
  },
  exportPage: {
    exportFormat: {
      [printableExportFormat.pdf]: 'PDF',
      [printableExportFormat.image]: 'PNG',
      [printableExportFormat.csv]: 'CSV',
    },
  },
  propertyApplication: {
    user: 'Users',
    goal: 'Goals',
    update: 'Check-ins',
    space: 'Spaces',
  },
  mContextMenu: {
    paragraph: {
      title: 'Text',
      subTitle: 'Just start writing with plain text',
    },
    heading1: {
      title: 'Heading 1',
      subTitle: 'Big heading.',
    },
    heading2: {
      title: 'Heading 2',
      subTitle: 'Medium heading.',
    },
    heading3: {
      title: 'Heading 3',
      subTitle: 'Small heading.',
    },
    link: {
      title: 'Link website',
      subTitle: 'Save a link to a website.',
    },
    blockquote: {
      title: 'Quote',
      subTitle: 'Insert a quote block.',
    },
    image: {
      title: 'Image',
      subTitle: 'Upload an image.',
    },
    file: {
      title: 'File',
      subTitle: 'Upload a file.',
    },
    bulletList: {
      title: 'Bulleted List',
      subTitle: 'Insert a list with bullet points.',
    },
    orderedList: {
      title: 'Ordered List',
      subTitle: 'Insert a numbered list.',
    },
    codeBlock: {
      title: 'Code',
      subTitle: 'Insert a code block.',
    },
    horizontalRule: {
      title: 'Divider',
      subTitle: 'Separate sections with a line.',
    },
    mention: {
      title: 'Mention',
      subTitle: 'Mention a workspace member.',
    },
    goalMention: {
      title: 'Link to {plural}',
      subTitle: 'Create a link to an existing {singular}.',
    },
    todoList: {
      title: 'To-Do List',
      subTitle: 'Track tasks with to-do lists.',
    },
    noItems: 'No block found.',
  },
  mFloatingMenu: { backslashHint: 'Type \'/\' for actions' },
  type: {
    goal: 'Goal',
    user: 'Employee',
    space: 'Team',
  },
  mobileMenuComponent: { turnInto: 'Turn into', add: 'Add block' },
  okta: {
    email: 'Okta Username',
    enterEmail: 'Enter your Okta username',
  },
  globalNewBtn: {
    update: 'Check-in',
    noItemFound: 'No item found',
  },
  publishButton: {
    header: 'Share to web',
    description: 'Publish and share link with anyone',
    copyLink: 'Copy link',
    copyEmbedCode: 'Copy embed code',
  },
  publishedView: {
    notPublished: {
      title: 'Page not found.',
      description: 'The public page you\'re looking for stopped being shared.',
    },
  },
  helpButton: {
    contactSupport: 'Contact support',
    chatWithSales: 'Chat with sales',
    productFeedback: 'Give product feedback',
    placeholderFeedback: 'Do you have any feedback or want to request a feature? Please describe your feedback or your feature request in as much detail as possible.',
    whatsNew: 'What\'s new',
    companyInfo: { emptyTitle: 'Company Info' },
    helpCenterLink: '@:{\'general.helpCenterBaseUrl\'}',
  },
  identityAndProvisioning: {
    security: { heading: 'Access' },
    restrictLogin: {
      passwordLoginTitle: 'Allow login via password',
      passwordLoginSubTitle: 'Allow users to log in using a password',
      deactivatePasswordTitle: 'Are you sure you want to prohibit users from logging in via password?',
      activatePasswordTitle: 'Are you sure you want to allow users to log in using a password?',
      googleLoginTitle: 'Allow login via Google',
      googleLoginSubTitle: 'Allow users to log in using their Google account',
      deactivateGoogleLoginTitle: 'Are you sure you want to prohibit users from being able to log in using their Google account',
      activateGoogleLoginTitle: 'Are you sure you want to allow users to log in using their Google account?',
      microsoftLoginTitle: 'Allow login via Microsoft',
      microsoftLoginSubTitle: 'Allow users to log in using their Microsoft account',
      deactivateMicrosoftLoginTitle: 'Are you sure you want to prohibit users from being able to log in using a Microsoft account',
      activateMicrosoftLoginTitle: 'Are you sure you want to allow users to log in using a Microsoft account?',
      selfLockoutNoMethodLeft: 'This would leave no way to log in to this account. At least one login method must stay enabled.',
      selfLockoutStepUp: 'You can\'t disable a login method you\'re currently relying on. Verify with another allowed method first.',
      selfLockoutVerify: 'Verify',
    },
    userProvisioning: {
      enable: 'Are you sure you want to enable user provisioning?',
      enableContent: 'User provisioning allows you to synchronize users between Mooncamp and a third-party identity provider (such as Okta, Microsoft Azure, ...)',
      disable: 'Are you sure to want to disable user provisioning',
      disableContent: 'User synchronization will be turned off and will need to be reconfigured afterwards.',
    },
    saml: {
      heading: 'SAML',
      ssoUrl: 'SSO-URL',
      entityId: 'Entity-ID',
      certificate: 'Certificate',
      save: 'Save',
    },
    contactLink: '@:{\'general.websiteBaseUrl\'}/contact/',
  },
  integrationSettings: {
    title: 'Integrations',
    subTitle: 'Enable or disable integrations for your workspace',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/data-integrations',
    help: 'Learn more about how to set up integrations.',
    messaging: { title: 'Messaging' },
    dataIntegrations: { title: 'Data integrations' },
    calendarIntegrations: { title: 'Calendar integrations' },
    slack: 'Slack',
    slackSubTitle: 'Receive personal notifications in Slack.',
    manage: 'Manage',
    msTeams: 'Microsoft Teams',
    msTeamsSubTitle: 'Receive personal notifications in MS Teams.',
    excelSubTitle: 'Connect your goals to a cell in an Excel Online document.',
    sheetsSubTitle: 'Connect your goals to a cell in a Google Sheets document.',
    hubspotSubTitle: 'Connect your goals with a Hubspot metric.',
    asanaSubTitle: 'Connect your goals with Asana tasks.',
    jiraSubTitle: 'Connect your goals with Jira tasks.',
    salesforceSubTitle: 'Connect your goals to Salesforce reports.',
    msPowerBiSubTitle: 'Connect your goals to Microsoft Power BI datasets.',
    msPlannerSubTitle: 'Connect your goals to Microsoft Planner tasks.',
    azureDevOpsSubTitle: 'Connect your goals to Azure DevOps work items.',
    install: 'Install',
    installed: 'Installed',
    syncDays: 'Synchronization',
    syncDaysTitle: 'Days of the week',
    syncDaysDescription: 'Select the days of the week on which the data should be synchronized.',
  },
  msTeamsInstallHint: {
    link: '@:{\'general.helpCenterBaseUrl\'}/microsoft-teams-integration',
    msTeamsGuide: 'How to connect Mooncamp to MS Teams',
  },
  msTeamsSwitch: {
    uninstallHint: 'To disconnect Mooncamp from MS Teams, you need to uninstall the Mooncamp app from MS Teams.',
    description: 'Enable the MS Teams integration so users can connect MS Teams and Mooncamp.',
  },
  slackSwitch: { description: 'Connect Slack with your Mooncamp workspace.' },
  dataSourceDetails: {
    back: 'Back to integrations',
    connections: 'Connections',
    connectionsDescription: 'Configure {type} connections that can be used by Mooncamp users to link their goals and update progress.',
    jira: {
      help: 'Learn more about the @:{\'dataSources.jira\'} integration.',
      helpLink: '@:{\'general.helpCenterBaseUrl\'}/jira-integration',
      description: 'Connect your goals with @:{\'dataSources.jira\'} tasks.',
    },
    sheets: {
      help: 'Learn more about the @:{\'dataSources.sheets\'} integration.',
      helpLink: '@:{\'general.helpCenterBaseUrl\'}/google-sheets-integration',
      description: 'Connect your goals with cells of @:{\'dataSources.sheets\'} documents.',
    },
    excel: {
      help: 'Learn more about the @:{\'dataSources.excel\'} integration.',
      helpLink: '@:{\'general.helpCenterBaseUrl\'}/microsoft-excel-integration',
      description: 'Connect your goals with cells of @:{\'dataSources.excel\'} documents.',
    },
    asana: {
      help: 'Learn more about the @:{\'dataSources.asana\'} integration.',
      helpLink: '@:{\'general.helpCenterBaseUrl\'}/asana-integration',
      description: 'Connect your goals with @:{\'dataSources.asana\'} tasks.',
    },
    hubspot: {
      help: 'Learn more about the @:{\'dataSources.hubspot\'} integration.',
      helpLink: '@:{\'general.helpCenterBaseUrl\'}/hubspot-integration',
      description: 'Connect your goals with @:{\'dataSources.hubspot\'} metrics.',
    },
    salesforce: {
      help: 'Learn more about the integration.',
      helpLink: '@:{\'general.helpCenterBaseUrl\'}/data-integrations',
      description: 'Connect your goals with @:{\'dataSources.salesforce\'} reports.',
    },
    msPowerBi: {
      help: 'Learn more about the @:{\'dataSources.msPowerBi\'} integration.',
      helpLink: '@:{\'general.helpCenterBaseUrl\'}/microsoft-power-bi-integration',
      description: 'Connect your goals to @:{\'dataSources.msPowerBi\'} datasets.',
    },
    msPlanner: {
      help: 'Learn more about the @:{\'dataSources.msPlanner\'} integration.',
      helpLink: '@:{\'general.helpCenterBaseUrl\'}/microsoft-planner-integration',
      description: 'Connect your goals to @:{\'dataSources.msPlanner\'} tasks.',
    },
    azureDevOps: {
      help: 'Learn more about the @:{\'dataSources.azureDevOps\'} integration.',
      helpLink: '@:{\'general.helpCenterBaseUrl\'}/azure-devops-integration',
      description: 'Connect your goals to @:{\'dataSources.azureDevOps\'} work items.',
    },
  },
  templateDetails: { noQuestions: 'No questions available' },
  oauth: { missingOpener: 'Look\'s like something went wrong.', retry: 'Retry' },
  fileExplorer: { empty: 'This folder is empty', root: 'My drive' },
  goalCascadeParents: { more: 'more' },
  userProvisioning: {
    heading: 'User provisioning',
    subheading: 'Synchronize Mooncamp users with a third party identity provider',
    token: 'API token',
    activate: 'Activate',
    actionTitle: 'Activate user provisioning',
    actionSubtitle: 'Activate user provisioning via the SCIM protocol',
    learnAbout: 'Learn about SCIM',
    attributeMapping: 'Attribute Mapping',
    attributeMappingDescription: 'Map SCIM Attributes to properties inside Mooncamp',
    addAttributeMapping: 'Add attribute mapping',
    property: 'Mooncamp property',
    space: 'Space',
    option: 'Option',
    attribute: 'SCIM attribute',
    update: 'Update',
    confirmAttributeDelete: 'Are you sure you want to remove this attribute mapping?',
    helpCenterLink: '@:{\'general.helpCenterBaseUrl\'}/scim-azure',
    scimAttributeValues: 'SCIM attribute values',
    scimAttributeType: 'SCIM Attribute type',
    moreItems: 'more',
    firstName: 'Name',
    lastName: 'Last Name',
    language: 'Language',
    email: 'Email',
    immutableField: 'This field is required and is therefore not modifiable.',
    syncSuccess: 'Users successfully synced',
    sync: 'Synchronize',
    syncLoading: 'Synchronization is running...',
    tooManySyncRequests: 'Only one syncronization operation is allowed per five minutes. Try again later.',
    delete: 'Delete Provisioning',
    deleteWarning: {
      text: 'This action will permanently delete this provisioning, including associated SCIM resources. Users created via provisioning will not be removed.',
      securityQuestion: 'Type in the company name to proceed',
    },
  },
  savedViewPrompt: { prompt: 'You have unsaved changes, do you want to discard them?' },
  alignedItem: { weightDescription: 'Select weight' },
  productTour: {
    author: 'from Mooncamp',
    previous: 'Back',
    next: 'Next',
    done: 'Done',
    of: 'of',
    home: {
      masteringMooncampStep: {
        heading: 'Execute your strategy with Mooncamp',
        content: `The <b>resource center</b> is your one-stop-shop to
            <ul>
            <li>watch Mooncamp tutorials</li>
            <li>get help from our support team</li>
            <li>access our help center</li>
            <li>learn more about Strategy and OKRs</li>
            </ul>`,
      },

      goalExplorerStep: {
        heading: 'Goal Explorer',
        content: 'In the Goal Explorer, you can browse all goals and create custom views with individual filters.',
      },
      planningStep: {
        heading: 'Plans',
        content: 'In plans, you can create goals with your team in draft mode, coordinate them with others, and publish them for everyone once finalized.',
      },

      goalCycleStep: {
        heading: 'Goals belong to a goal cycle',
        content: 'Goals or OKRs always belong to at least one goal cycle. This is where you can switch between cycles.',
      },
      checkInStep: {
        heading: 'Regular Check-ins',
        content: 'Scheduling recurring check-ins and reminders to update your goals is important to keep everyone engaged in the process. They let everyone see what others are working on, discuss progress and where mutual support is needed.',
      },
      dashboardStep: {
        heading: 'Dashboards',
        content: 'Use dashboards to get deeper insights into the progress of your goals, the health of your goal setting process, or to evaluate the overall development at the end of each cycle.',
      },
      personalHomePageStep: {
        heading: 'Your personal space',
        content: 'View your goals, check-ins, recent updates, or create your own custom views here.',
      },
      settingsStep: {
        heading: 'Settings',
        content: 'Enable integrations, customize your OKR system, invite co-workers, set permissions or access advanced features here.',
      },
      spacesStep: {
        heading: 'Teamspaces',
        content: 'Each team has its own dedicated space to showcase goals and check-ins.\n\nYou can explore the organizational structure and pin your key teams to the sidebar for quick access.',
      },
    },
    checkins: {
      step1: {
        heading: 'This is a check-in',
        content: 'Check-ins from all teams will appear in this feed by default. Check-ins belonging to a team can also be found on your team pages.',
      },
      step2: {
        heading: 'Create new check-ins here',
        content: 'You can create a new check-in here from scratch or by choosing a custom template.',
      },
      step3: {
        heading: 'Create and edit schedules',
        content: 'You can create new or edit existing check-in schedules here. Depending on their notification preferences, recipients will receive a notification via email, Slack, or MS Teams.',
      },
    },
  },
  discoverMooncamp: {
    title: 'Learn why Mooncamp is unique',
    learnMore: 'Learn more',
    properties: {
      heading: 'Customize anything with Properties',
      description: 'Properties are one of the most exciting features of Mooncamp, allowing your organization to completely customize the Mooncamp platform to fit your needs, internal processes, and company language.',
    },
    powerful: {
      heading: 'Light-weight but powerful',
      description: 'Simple and intuitive design that drives adoption among all teams.',
    },
    integrations: {
      heading: 'Microsoft Teams and Slack Integration',
      description: 'Mooncamp can be embedded into MS Teams or Slack, allowing you to get notifications and manage OKRs directly from your collaboration tools.',
    },
    okrRules: {
      heading: 'Use rules to customize the goal system to your processes',
      description: 'In Mooncamp, you can not only create your own goal types (such as {tag1}, {tag2}, or {tag3}) but also customize them with rules you define yourself. Rules can be, for example:',
      bp1: '{tag1} may only be aligned to {tag2}',
      bp2: '{tag1} may only be aligned to {tag2}',
      bp3: '{tag1} must always have an {tag2}',
      bp4: '{tag1} may only be measured by {tag2}',
      keyResults: 'Key Results',
      objectives: 'Objectives',
      strategicPillars: 'Strategic Pillars',
      owner: 'Owner',
      initiatives: 'Initiatives',
      openDone: 'Open/Done',
    },
  },
  peekView: {
    peekModes: 'Peek modes',
    sidePeek: 'Side peek',
    centerPeek: 'Center peek',
    productTour: {
      heading: 'New side peek view',
      body: 'Goal pages now open in the new side peek mode by default. If you prefer the old center peek mode, you can switch back here. This is a personal setting and will not affect other users.',
    },
  },
  afterAccountSpaceDemoModal: {
    congratulations: 'Congratulations',
    description: 'You successfully completed Mooncamp’s product tour!',
    goToOnboarding: 'Go to onboarding guide',
    explore: 'Explore on your own',
  },
  afterCheckinTourModal: {
    congratulations: 'Congratulations',
    description: 'You successfully completed Mooncamp’s check-ins tour!',
    cta: 'Next, create your first check-in:',
    create: 'Create first check-in',
    explore: 'Skip and explore on your own',
  },
  updateDivider: {
    lastWeek: 'Last week',
    weekOf: 'Week of {date}',
  },
  mMentionMenu: {
    users: 'Persons',
    noItems: 'No persons or {title} found.',
    noUsers: 'No persons found',
    noGoals: 'No {title} found.',
    goalsTitle: 'Link to {title}',
  },
  goalMentionComponent: { hint: '{title} has either been deleted or you do not have read permission.' },
  goalActivityForm: {
    referencedHint: 'Reference to a past update',
    done: 'Done',
  },
  browserNotSupported: {
    title: 'Browser not supported',
    hint: 'You are using an outdated browser that is not supported by Mooncamp. You can use any of the following: Chrome, Edge, Firefox, or Safari.',
  },
  durationUnit: {
    minute: 'minute | minutes',
    hour: 'hour | hours',
    day: 'day | days',
  },
  afterUnit: {
    minute: 'minute | minutes',
    hour: 'hour | hours',
    day: 'day | days',
  },
  reminderEditor: { after: 'After' },
  reminderList: {
    noReminders: 'No reminders available',
    add: 'Add reminder',
  },
  plans: {
    title: 'Plans and billing',
    enterprise: 'Enterprise',
    pro: 'Professional',
    business: 'Essential',
    short: {
      enterprise: 'Enterprise',
      pro: 'Pro',
      business: 'Essential',
    },
    learnMore: 'Learn more about our plans.',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/billing',
    portalButton: 'Manage billing and invoices',
  },
  pricingTable: {
    downgrade: 'Downgrade',
    upgrade: 'Upgrade',
    perMonthFlat: 'per month (flat)',
    perUserPerMonth: 'per user per month',
    currentPlan: 'Current plan',
    talkToSales: 'Contact Sales',
    letsTalk: 'Let\'s talk',
    workspace: 'Workspace',
    numberOfUsers: 'Maximum number of users',
    currentPlanHint: 'This is your current plan.',
    planDowngradeHint: 'If you want to downgrade your plan, please contact our sales team.',
    unlimited: 'Unlimited',
    goalsAndOkrs: 'Goals and OKRs',
    unlimited_okrs: 'Unlimited goals, initiatives, and hierarchy levels',
    customizability: 'Completely customizable goal system',
    private_goals: 'Private goals and OKRs',
    messengers: 'Slack and Microsoft Teams integration',
    integrations: 'Data integrations (Jira, Asana, Hubspot, ...)',
    planningSpaces: 'Plans',
    planningSpacesTooltip: 'Dedicated spaces for your teams to create goal drafts for the next cycle.',
    goalTree: 'Goal tree',
    goalTreeTooltip: 'Give everyone a high-level overview of your company’s strategy and the alignment of your goals and OKRs.',
    okrRules: 'Goal and OKR rules',
    okrRulesTooltip: 'Set rules to configure your own OKR system and hierarchy, mark fields as required, and other settings.',
    analytics: 'Dashboards & Analytics',
    dashboards: 'Progress Dashboard',
    premiumDashboards: 'Health Dashboard',
    premiumDashboardsTooltip: 'A comprehensive health and quality check of all your organization\'s goals',
    privateCustomDashboards: 'Private Dashboards',
    privateCustomDashboardsTooltip: 'Dashboards offer a versatile range of applications to support your strategy or OKR process, like Quarterly Business Reviews, Weekly Management Reports, Vision/Mission/Values boards, Company KPIs, and many more.',
    publicCustomDashboards: 'Shared Dashboards',
    publicCustomDashboardsTooltip: 'Dashboards offer a versatile range of applications to support your strategy or OKR process, like Quarterly Business Reviews, Weekly Management Reports, Vision/Mission/Values boards, Company KPIs, and many more.',
    customCharts: 'Custom Charts',
    customChartsTooltip: 'Build your own visualizations to track strategy performance metrics in the way that fits your business.',
    multiAlignment: 'Multi-alignment of OKRs',
    multiAlignmentTooltip: 'Align a goal with two or more parent goals.',
    premiumIntegrations: 'Premium data integrations (Salesforce, ...)',
    premiumIntegrationsTooltip: 'Custom integrations can be developed after consultation.',
    tvMode: 'Share public links to views (TV mode)',
    tvModeTooltip: 'Share a public link to a goals view with anyone who is not in your Mooncamp workspace.',
    goalWeights: 'Goal weighting',
    goalWeightsTooltip: 'Assign weights to your key results or subgoals.',
    checkIns: 'Check-ins',
    reminders: 'Create check-ins and reminders',
    checkInTemplates: 'Customizable templates',
    checkInMessengers: 'Check-in reminders in Slack and Microsoft Teams',
    admin: 'Administration and Security',
    adminGoogleSso: 'Google Single Sign-On',
    adminMsSso: 'Microsoft Single Sign-On',
    customSaml: 'Custom SAML-based SSO',
    scimProvisioning: 'SCIM Provisioning',
    support: {
      title: 'Support',
      helpcenter: 'Self-serve knowledge base',
      level: 'Support Level',
      levelStandard: 'Standard',
      levelPriority: 'Priority',
      levelPriorityTooltip: 'We offer Priority support for all workspaces with an annual contract value of €10,000 or above.',
      onboarding: 'Personalized onboarding',
      rollout: 'Personal guidance during implementation and rollout',
      sla: 'Service Level Agreement',
      account_manager: 'Dedicated account manager',
      contract: 'Custom contract and invoicing',
      dpa: 'Custom DPA',
    },
    upgradeModal: {
      title: 'Upgrade your workspace',
      text: 'If you want to subscribe to a plan, or have any questions regarding pricing, please contact our sales team: {mailTo}',
    },
  },
  planHint: {
    currentPlan: 'You workspace is currently on the {currentPlan}.',
    expiresAt: 'Your subscription expires on {expirationDate}.',
    renewsAt: 'Your subscription will be renewed on {expirationDate}.',
    trial: 'Trial Plan',
    starter: 'Starter Plan',
    business: 'Essential Plan',
    pro: 'Pro Plan',
    enterprise: 'Enterprise Plan',
    trialExpiresAt: 'Your trial expires on {expirationDate}.',
  },
  updateSchedules: { allSchedules: 'All Schedules' },
  schedulesTable: {
    name: 'Name',
    schedule: 'Cadence',
    participants: 'Participants',
    template: 'Template',
    participation: 'Participation',
    participationHint: 'Participation rate of the current execution.',
    createHeading: 'Create schedule',
    editHeading: 'Edit schedule',
    recipientsList: 'Participants',
    status: 'Status',
  },
  scheduleDetailPageHeader: { creatorDescription: 'Creator of this schedule' },
  participationList: {
    missing: 'Pending',
    done: 'Done',
  },
  scheduleDetailPage: {
    reminders: 'Reminders',
    noReminders: 'None',
    pausedAt: 'Paused at {date}',
    lastMonths: 'Last {months} months',
    participationRate: 'Participation rate',
    backToAllSchedules: 'Back to all schedules',
    notFound: 'Schedule not found',
    noSendOut: 'Scheduled notifications lie in the past.',
    nextSendOut: 'Next notification scheduled on: {date}.',
    sendOutHint: 'It is likely that this notification will not be sent, as it takes about 20 minutes for the notification to be fully set up.',
    at: 'at',
  },
  participantsTable: {
    title: 'Participants',
    user: 'Name',
    participated: 'Status',
    actions: 'Actions',
    view: 'View',
  },
  participationChart: { participation: 'Participation' },
  templateItem: {
    privateHint: 'This template is only visible to you.',
    sharedHint: 'This template is visible to you and others.',
    publicHint: 'This template is visible to everyone in your workspace.',
  },
  updateTemplates: { all: 'All Templates' },
  scheduleDetailTop: {
    allRecipients: 'All participants',
    allRecipientsDescription: 'All participants of this schedule',
  },
  spaceSelector: {
    mySpaces: 'My {title}',
    activeSpaces: 'Active {title}',
    archivedSpaces: 'Archived {title}',
    selectSpace: 'Select {title}',
    further: '+{amount} more',
    empty: 'No {title} found',
    searchPlaceholder: 'Search for {title}...',
    selectPlaceholder: 'Select {title}...',
    restrictForeignEntityReferences: {
      label: 'Limited by teams\' permissions',
      tooltip: 'You can only select from teams whose resources you can edit.',
    },
  },
  spaceSelectorItem: {
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
  },
  supportForm: {
    heading: 'Get in touch with our support team',
    description: 'We are happy to assist you with any issues you may have. Please send us an email at {supportEmail}',
    helpcenter: {
      tip: 'Small tip: Many questions can be quickly solved by taking a look at our Help Center, why not check it out 😊',
      action: 'Go to Helpcenter',
    },
  },
  upgradeForm: {
    heading: 'Get in touch with sales',
    description: 'We will help you select the right plan and set up your workspace.',
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
    emailLabel: 'Your work email',
    licencesLabel: 'Number of licenses',
    plansLabel: 'Plan',
    messageLabel: 'Your message',
    optional: '(optional)',
    questionLabel: 'Request',
    submittedTitle: 'Sent successfully',
    submittedSubTitle: 'Your message was sent successfully. A sales representative will contact you shortly via email.',
    subscriptionExpired: 'Trial period expired',
    subscriptionExpiredText: 'If you want to subscribe to one of our plans, extend your trial period or have any question regarding Mooncamp\'s software, please contact us.',
    required: 'This is a required field',
    scheduleDemo: 'Schedule a demo',
    bookPlan: 'Upgrade plan',
    other: 'Other',
    address: 'Company address',
    vatID: 'VAT ID (optional)',
    addressPlaceholder: 'Full company name and address',
    bookPlanExplanation: 'Please select a plan and provide your billing information. We will then prepare a quote for you to e-sign. Once signed, your subscription will start and you will receive an invoice.',
    scheduleDemoExplanation: 'We would be glad to guide you through Mooncamp in a personalized demo. Please fill out the demo form on our website and select a timeslot that suits you best.',
    scheduleDemoButton: 'Select a demo slot',
    scheduleDemoLink: '@:{\'general.websiteBaseUrl\'}/contact/',
    invalid: 'Please fill in all required fields.',
  },
  spaceItem: {
    unpinHelp: 'Remove from sidebar',
    pinHelp: 'Add to sidebar',
    disabledHelp: 'You cannot remove {name} from the sidebar because you are a member',
  },
  spaceNavigation: { searchPlaceholder: 'Search for {title}...' },
  msError: {
    header: 'This Teams account is already connected to another Mooncamp user',
    message: 'Your Microsoft Teams account is already connected to the Mooncamp user {email}. A Teams account can only be connected to one Mooncamp user at a time, so connecting it here won\'t work until you disconnect the existing connection first.',
    message2: 'To fix this: log in as {email}, open Settings → Notifications and click \'Disconnect\', then come back and connect again. If {email} isn\'t you, let us know at {supportmail}.',
    goBack: 'Go back',
  },
  propertyLabel: {
    editProperty: 'Edit property',
    hideProperty: 'Hide property',
    customizePage: 'Customize page',
    alwaysVisible: 'Always visible',
    hideWhenEmpty: 'Hide when empty',
    alwaysHide: 'Always hide',
  },
  propertyValues: {
    hiddenProperties: 'more property | more properties',
    hideProperties: 'Hide 1 property | Hide {count} properties',
  },
  customizePage: {
    header: 'Detail page for',
    properties: 'Properties',
    goalChildrenProperties: 'Subitem properties',
  },
  cascadeTableRow: {
    editTitle: 'Edit title',
    errorTitle: 'Validation errors',
  },
  documentListHeader: {
    wrapCells: 'Wrap cells',
    wrapTitles: 'Wrap goal titles',
    wrapTitlesTooltip: 'Wraps goal titles mentioned in updates.',
    expandNextLevel: 'Expand next level',
    expandAll: 'Expand all',
    collapseNextLevel: 'Collapse next level',
    collapseAll: 'Collapse all',
    locked: 'Locked',
    lockedHint: 'You don\'t have sufficient rights to edit this view.',
    loadLimit: 'Load limit',
    loadLimitRoot: 'Load limit (root elements)',
  },
  parentAlignment: {
    label: 'Parent alignment',
    hidden: 'Hidden',
    hiddenDescription: 'Hide parent goals entirely. Only goals matching your filters are shown, as a flat list.',
    direct: 'Show direct parents',
    directDescription: 'Nest each matching goal under its immediate parent only. Ancestors further up stay hidden.',
    full: 'Show full hierarchy',
    fullDescription: 'Show the complete chain of parents — every goal linking a match up to its top-level parent, even when those in-between goals don\'t match your filters.',
    minimal: 'Hide in-between goals',
    minimalDescription: 'Hide goals that don\'t match your filters, even when they sit between two matches. Each match is nested under its nearest matching parent, and a marker shows where in-between goals were hidden.',
    indirectTooltip: 'Collapsed connection: this isn\'t the real parent. The goals in between don\'t match the current filter, so they\'re hidden and this goal was moved up to its nearest matching parent.',
  },
  lookupForm: {
    relationTitle: 'Goal property',
    propertyTitle: 'User property',
    from: 'from',
    title: 'Configure property',
    relationInfo: 'The goal property of type Person (e.g., "Owner") through which the value is looked up.',
    propertyInfo: 'The user property whose value or content is returned.',
  },
  foreignSpaceReference: { insufficientPermissions: 'You are only allowed to create resources for your own teams.' },
  companyInfo: {
    title: 'Info page',
    help: 'Learn more about info pages.',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/info-page',
    actions: {
      activate: {
        title: 'Show in help area',
        subTitle: 'The page is displayed to all employees in the help area.',
      },
      onboarding: {
        title: 'Show during Onboarding',
        subTitle: 'The page is displayed to all employees when they log in for the first time.',
      },
      open: 'Open',
    },
    contentHeader: 'Content',
    prompt: 'You have unsaved changes, do you want to discard them?',
    saveChanges: 'Save changes',
  },
  goalStatusTag: {
    disabledAutoStatusUpdateDescription: 'Automated status update is currently disabled. Enabling this setting will update the status based on this goal\'s subitems.',
    enableAutomaticStatusUpdate: 'Enable automated status update',
    successfullyActivated: 'Automated status update enabled.',
    successfullyDeactivated: 'Automated status update disabled.',
  },
  releaseNotesWrapper: {
    changelog: 'Changelog',
    fullChangelog: 'See full changelog',
    empty: 'There are no release notes to show right now.',
    loadFailed: 'The release notes couldn\'t be loaded. Please try again later.',
    releaseNotesLink: '{baseURL}/en/releases',
  },
  planningList: {
    pageTitle: 'Plans',
    helpText: 'Learn more about plans',
    helpLink: '@:{\'general.helpCenterBaseUrl\'}/plans',
    title: 'Name',
    creator: 'Created by',
    createdAt: 'Created on',
    goalCycle: 'Cycle',
    modifiedAt: 'Last updated',
    onboarding: {
      title: 'What is a plan?',
      drafts: 'goal drafts',
      description1: 'It is a dedicated space where you can create {title} with your team before they are published for everyone to see.',
      description2: 'Planning is a core element of the OKR process and usually takes place before the next cycle starts.',
      description3: 'Learn more about plans in our {helpCenter}.',
      helpCenterLink: '@:{\'general.helpCenterBaseUrl\'}/plans',
      helpCenter: 'help center',
      createPlanning: 'Create a plan',
      notNow: 'Not right now',
    },
  },
  plannings: {
    open: 'Open',
    closed: 'Closed',
    all: 'All',
  },
  planningForm: {
    createTitle: 'Create a plan',
    titleLabel: 'Name',
    titlePlaceholder: 'Enter a title...',
    cyclePlaceholder: 'Select a cycle...',
  },
  planningDetails: {
    planning: 'Plan',
    plannings: 'Plans',
    publishGoals: 'Publish {title}',
    publish: 'Publish',
    publishSelected: 'Publish {amount} items',
    closePlanning: 'Close plan',
    markClosed: 'Mark as closed',
    reopen: 'Reopen',
    closed: 'Closed',
    published: 'Your {title} have been published.',
    nextStepsHint: 'Click to see where your {title} have been published:',
    shareCreatorDescription: 'The creator of this plan',
    importGoals: {
      initialModal: {
        title: 'Start from scratch or import {title}',
        scratch: 'Start from scratch',
        import: 'Import {title}',
      },
      button: 'Import {title}',
      modal: {
        title: 'Import {title} as duplicates',
        button: 'Import {amount} items',
      },
    },
    productTour: {
      step1: {
        heading: 'Plan properties',
        content: 'A plan is usually associated with a cycle and a team.\n\nGoal drafts created in this plan will have these values applied per default.',
      },
      step2: {
        heading: 'Sharing settings',
        content: 'Per default, a plan is shared with the team(s) it is associated with. You can always change with whom it is shared here.',
      },
      step3: {
        heading: 'Publish goal drafts',
        content: 'Once you are done with the planning session, you can select which goals to publish here.',
      },
    },
  },
  planningsContextMenu: {
    cannotDeleteMultiple: 'You are not allowed to delete the selected items.',
    deleteHint: 'You are not allowed to delete all of the selected items.',
  },
  goalPage: {
    notPublishedHint: 'This {title} is not published yet.',
    openPlanning: 'Open plan',
  },
  slack: {
    connectionError: {
      goBack: 'Go back',
      header: 'Error during connection with Slack',
      missingAuth: 'You do not seem to have the required permissions to connect Mooncamp to a Slack workspace. If you just want to connect your Mooncamp workspace to Slack, go to your Slack workspace and click on the Mooncamp Slack app.',
      defaultError: 'Unfortunately there has been an error during connecting with Slack. Please contact us via {supportmail}',
    },
  },
  appFeedback: {
    npsQuestion: 'How likely are you to recommend Mooncamp to a colleague or business contact?',
    npsIntro: 'Takes 5 seconds. We read every answer and act on it.',
    dontAskAgain: 'Don\'t ask me again',
    npsScale: {
      left: 'Not likely',
      right: 'Very likely',
    },
    followUp: {
      detractor: 'Sorry to hear that. What should we improve first?',
      passive: 'What would make it a 10?',
      promoter: 'Great to hear! What do you value most?',
    },
    hint: 'Optional – a few words are enough',
    thankYou: {
      detractor: 'Thanks for being honest. We\'ll look into it.',
      passive: 'Thanks! This helps us get better.',
      promoter: 'Thank you! 🙏',
    },
    referral: 'Would you share that on {name}?',
    anytimeHint: 'You can share more feedback anytime via {icon} in the navigation.',
  },
  setParents: {
    prompt: 'Should this goal contribute to the progress of {goals}? | Should these goals contribute to the progress of {goals}?',
    notSetPaysOnto: 'No',
    setPaysOnto: 'Yes',
  },
  goalCycles: {
    title: 'Goal cycles',
    learnMore: 'Learn more about Goal cycles.',
    helpCenterLink: '@:{\'general.helpCenterBaseUrl\'}/cycles',
  },
  goalCyclesTable: {
    title: 'Title',
    start: 'Start date',
    end: 'End date',
  },
  goalType: {
    title: 'Goal types and rules',
    learnMore: 'Learn more about Goal and OKR rules.',
    helpCenterLink: '@:{\'general.helpCenterBaseUrl\'}/goal-rules',
    listTitle: 'Any',
    editTitle: 'Edit type',
    singleRequired: 'This goal type is required and cannot be deleted.',
    oneOrMoreRequired: 'One or more selected goal types are required and cannot be deleted.',
  },
  goalStatus: {
    title: 'Status',
    learnMore: 'Learn more about Statuses.',
    helpCenterLink: '@:{\'general.helpCenterBaseUrl\'}/goal-statuses',
    subTitle: 'Configure the statuses of your goals.',
  },
  propertyMenu: { goalTypeRequired: 'Editing only possible if a goal type is set' },
  goalTypeForm: {
    properties: 'Properties',
    deleteEmptyPrompt: 'The goal type is emtpy. Would you like to delete it?',
  },
  propertySettingsForm: {
    edit: 'Edit {title}',
    visibility: 'Visibility',
    canBeChildOf: 'Can be child of',
    required: 'Required',
    progressMeasurement: 'Measurement',
  },
  propertyValidation: { invalidParentType: 'Allowed type is: {types} | Allowed types are: {types}' },
  validationErrors: {
    header: 'There are {amount} validation errors:',
    canBeChildOf: 'The type of the parent "{title}" should be {expectedTypes}, but is {actualTypes}.',
  },
  measurementTypeError: { allowedTypes: 'Allowed measurement types:' },
  canBeChildOfError: { allowedTypes: 'Allowed goal type: | Allowed goal types:' },
  requiredError: { title: 'Required field' },
  mustBeEmptyError: { title: 'Must be empty.' },
  goalTypeList: {
    deletePrompt: 'Are you sure, you want to delete this goal type?',
    yes: 'Delete goal type',
  },
  goalCreateDropdown: { title: 'Create a goal' },
  rGoalsCascadeTable: { cannotChangeOrder: 'Order cannot be changed because write permissions are missing.' },
  goalMutationErrors: {
    circularReference: 'The goals you are trying to align form a circular reference, which is not permitted.',
    missingAccessRightsGoal: 'Missing access rights for goal "{title}"',
    missingAccessRightsView: 'Missing access rights for view "{title}"',
  },
  goalSort: {
    removeExistingSort: 'You already have an existing sort. Do you want to remove it?',
    removeExistingSortButton: 'Remove sort',
  },
  goalProperties: { noParentAllowed: 'Parent alignment must be empty.' },
  iconList: {
    icons: 'Icons',
    recent: 'Recent',
    people: 'People',
    nature: 'Animals and Nature',
    food_drink: 'Food and Drink',
    activity: 'Activity',
    travel: 'Travel and Places',
    objects: 'Objects',
    symbols: 'Symbols',
    flags: 'Flags',
    pickAColor: 'Pick a color',
  },
  iconPicker: {
    search: 'Type to search',
    types: {
      emoji: 'Emojis',
      icon: 'Icons',
      custom: 'Custom',
    },
    remove: 'Remove',
    title: 'Select emoji',
    placeholderTooltip: 'Select an icon',
    placeholderTooltipEmoji: 'Select an emoji',
    custom: {
      upload: 'Upload image',
      recommended: 'Recommended size is 280 x 280 pixels',
      cropImage: 'Crop selected image',
    },
  },
  userProvisioningStatus: {
    provisionedUserCount: 'Users available',
    userCount: 'Users synchronized to Mooncamp',
    updatesAvailable: 'Updates available',
    errors: 'Synchronization errors',
    showErrors: 'Show errors',
    flaggedForArchiving: 'Users marked for deactivation',
    archivedCount: 'Users deactivated',
  },
  userProvisioningErrors: {
    givenName: 'givenName',
    familyName: 'familyName',
    email: 'email',
    error: 'Synchronization error',
    title: 'Synchronization errors',
    externalId: 'IDP ID',
  },
  oauthCodeGrant: {
    actionTitle: 'Activate OAuth authentication',
    actionSubtitle: 'Activate OAuth authentication for Azure based user provisioning applications ',
    clientId: 'Client ID',
    clientSecret: 'Client Secret',
    redirectUrls: 'Redirect URLs',
    addRedirectUrl: 'Add Redirect URL',
    save: 'Save',
  },
  oauthCodeGrantRedirect: {
    header: 'Sign in',
    message: 'Sorry, but we\'re having trouble signing you in.',
    message2: 'If the problem persists, please contact us via {supportmail} and include the current time and the error below.',
    internalError: 'Internal error',
  },
  planTag: {
    activateHint: 'Activate this feature. Click to learn more.',
    upgradeHint: 'Upgrade to use this feature. Click to learn more.',
  },
  featureGateDialogs: {
    healthDashboard: {
      subTitle: 'Health dashboard: A comprehensive quality check of all your goals',
      description: 'With the Health dashboard, you can do a comprehensive health and quality check on all of your organization’s goals. Consider it a cockpit from where you can quickly identify and solve any issues with your goals. This helps to ensure that your whole goal management process is running smoothly and that everyone follows the Goal and OKR rules that you configured in the settings area.',
      actions: { learnMoreLink: '@:{\'general.helpCenterBaseUrl\'}/health-dashboard' },
      image: { alt: 'Health dashboard' },
    },
    customDashboards: {
      subTitle: 'Custom dashboards',
      description: 'Build custom dashboards for a variety of use cases, such as:',
      bullet1: 'Company Cockpit: Executive summary of your company\'s vision, mission, and values, as well as top-level KPIs and overall OKR progress.',
      bullet2: 'Quarterly Business Review: An in-depth analysis of achievements, challenges, and strategic initiatives.',
      bullet3: 'Personal Dashboard: A tailored view featuring your OKRs, check-ins, personal notes and file attachments.',
      bullet4: 'Team Overview: A snapshot of a teams purpose, mission, key metrics, goals, and team members.',
      bullet5: 'Plus many more use cases...',
      actions: { learnMoreLink: '@:{\'general.helpCenterBaseUrl\'}/dashboards' },
      image: { alt: 'Custom dashboards' },
    },
    customCharts: {
      subTitle: 'Custom Charts',
      description: 'Answer the questions that matter: Which teams are on track? Where is progress stalling? Are check-ins happening consistently?',
      bullet1: 'Dive deep into your data with column, bar, line, donut, metric, and table charts. Group, split, and filter by team, owner, status, cycle, type, or any custom property.',
      bullet2: 'Give leadership real-time visibility into strategic progress with board-ready dashboards built from live data, no manual reporting.',
      actions: { learnMoreLink: 'https://mooncamp.com/docs/custom-charts' },
      image: { alt: 'Custom charts' },
    },
    goalTree: {
      subTitle: 'Goal Tree: Get a birds-eye view of your strategy',
      bullet1: 'Use the goal tree view as a drawing board to map out all your strategic pillars, focus areas, goals, and initiatives in a hierarchical view.',
      bullet2: 'Give everyone a high-level overview of your company\'s strategy and the alignment of your goals and OKRs.',
      bullet3: 'Zoom in on OKR sets of certain departments or focus areas.',
      bullet4: 'Quickly discover isolated goals and align them under the correct parent goal.',
      image: { alt: 'Goal tree' },
    },
  },
  planGateDialogs: {
    shared: {
      title: 'You need to upgrade to use this feature',
      actions: {
        upgradePlan: 'Upgrade to the {plan} plan',
        comparePlans: 'Compare all plans',
        comparePlansWebsiteLink: '@:{\'general.websiteBaseUrl\'}/pricing/',
        activateFeature: 'Activate this feature',
        contactAdminToUpgrade: 'Interested? Ask your workspace administrator to upgrade your plan.',
        contactAdminToActivate: 'Interested? Ask your workspace administrator to activate this feature.',
        learnMore: 'Learn more about this feature',
      },
    },
    pro: {
      subTitle: 'Discover the benefits of the Pro plan',
      description: {
        bulletIntro: 'Here’s what you get with Mooncamp Pro.',
        bulletHealth: 'Health dashboard: A comprehensive quality check of all your goals',
        bulletOKRRule: 'Goal rules: Set rules to configure your own goal system, define the hierarchy of goals, and mark properties as required',
        bulletGoalWeight: 'Goal weighting: Assign weights to your subgoals or Key Results',
        bulletMultiAlignment: 'Multi-alignment of goals: Let a goal contribute to two or more parent goals',
        bulletGoalTree: 'Goal tree: Display your goals in a hierarchical tree view and get a birds-eye view of your strategy.',
        bulletMore: 'And much more.',
      },
      image: { alt: 'Pro plan' },
    },
    enterprise: {
      subTitle: 'Discover the benefits of the Enterprise plan',
      description: {
        bulletIntro: 'Here’s what you get with Mooncamp Enterprise. Everything in the Pro Plan, plus:',
        bulletPublishedViews: 'Public Links: Share public links with people outside of your workspace',
        bulletScim: 'SCIM Provisioning: Access the Mooncamp SCIM API to provision and manage users and groups',
        bulletSaml: 'SAML SSO: Manage employee access at scale with secure single sign-on',
        bulletSupport: 'Dedicated Account Manager and Priority Support',
        bulletMore: 'And much more.',
      },
      image: { alt: 'Enterprise plan' },
    },
  },
  maintenance: {
    header: 'We\'ll be back soon!',
    message: 'Sorry for the inconvience. We\'re performing some maintenance at the moment.',
  },
  resourceCenter: { title: 'Resources' },
  progressTag: { completed: 'completed' },
  rcOverview: {
    videos: {
      title: 'Video Tutorials',
      subTitle: 'Getting started with Mooncamp',
      watchDemoLink: 'https://mooncamp.com/watch-demo/',
    },
    videoFAQs: {
      title: 'FAQs',
      subTitle: 'Frequently asked questions',
      link: '@:{\'general.helpCenterBaseUrl\'}/faqs',
    },
    onboardingGuide: { title: 'In-App Onboarding', subTitle: 'First steps & workspace setup' },
    resources: {
      title: 'OKR Resources',
      subTitle: 'OKR infos & best practices',
    },
    helpCenter: { title: 'Help Center', subTitle: 'Detailed infos about features and workspace settings' },
    gettingStarted: {
      title: 'Getting started guide',
      subTitle: 'Become a Mooncamp expert',
    },
    legalInformation: 'Legal information',
    legalInformationLink: '@:{\'general.websiteBaseUrl\'}/legal-information',
  },
  okrResources: {
    hint: 'Please note: Every company is different and uses their own OKR model, with different rules and vocabulary. Therefore, while reading the resources below, keep in mind that the content might differ from your team’s or organization’s understanding of OKRs and how they are implemented.',
    successStories: {
      title: 'Success Stories',
      subTitle: 'How our customers use Mooncamp successfully',
      href: '@:{\'general.websiteBaseUrl\'}/customers',
    },
    okrGuide: {
      title: 'The complete OKR guide',
      subTitle: 'A guide on the OKR method',
      href: '@:{\'general.websiteBaseUrl\'}/okr/',
    },
    howtoWriteOkrs: {
      title: 'How to write OKRs',
      subTitle: 'Tips for writing great OKRs',
      href: '@:{\'general.websiteBaseUrl\'}/blog/how-to-write-okrs/',
    },
    okrRolloutGuide: {
      title: 'The OKR rollout guide',
      subTitle: 'Best practices to roll out OKRs',
      href: '@:{\'general.websiteBaseUrl\'}/blog/okr-implementation/',
    },
    okrCycle: {
      title: 'The OKR Cycle',
      subTitle: 'An overview of all OKR events',
      href: '@:{\'general.websiteBaseUrl\'}/blog/okr-cycle/',
    },
    okrPlanning: {
      title: 'The OKR Planning',
      subTitle: 'How to run an OKR planning session',
      href: '@:{\'general.websiteBaseUrl\'}/blog/okr-planning/',
    },
    okrCheckin: {
      title: 'The OKR Check-in',
      subTitle: 'How to run an OKR check-in',
      href: '@:{\'general.websiteBaseUrl\'}/blog/okr-check-in/',
    },
    okrRetrospective: {
      title: 'The OKR Retrospective',
      subTitle: 'How to run an OKR retrospective',
      href: '@:{\'general.websiteBaseUrl\'}/blog/okr-retrospective/',
    },
    review: {
      title: 'The OKR Review',
      subTitle: 'How to conduct an OKR review',
      href: '@:{\'general.websiteBaseUrl\'}/blog/okr-review/',
    },
    scoreOkrs: {
      title: 'How to score OKRs',
      subTitle: 'Scoring and grading OKRs',
      href: '@:{\'general.websiteBaseUrl\'}/blog/okr-scoring/',
    },
    benefits: {
      title: 'The 9 benefits of using OKR',
      subTitle: 'Why OKRs are so popular',
      href: '@:{\'general.websiteBaseUrl\'}/blog/okr-benefits/',
    },
    okrVsKpi: {
      title: 'OKR vs KPI',
      subTitle: 'What is the difference?',
      href: '@:{\'general.websiteBaseUrl\'}/blog/okr-vs-kpi/',
    },
    outcomeVsOutput: {
      title: 'Outcome vs Output',
      subTitle: 'What is the difference?',
      href: '@:{\'general.websiteBaseUrl\'}/blog/output-vs-outcome/',
    },
    examples: {
      title: 'OKR examples',
      subTitle: 'A collection of real-world OKRs',
      href: '@:{\'general.websiteBaseUrl\'}/okr-examples/',
    },
  },
  videos: { title: 'Videos' },
  productSurveyDialog: {
    icons: '🎄 🎁 ☃️',
    title: 'Yearly Product Feedback Survey',
    content: 'Tell us what features you want to see next in Mooncamp 🚀',
    callToAction: 'Take survey',
    hint: 'Takes less than 5 minutes.',
    surveyLink: 'https://mooncamp.typeform.com/to/z7PP1W2v',
    cancel: 'I don\'t want to take part',
  },
  onboardingSlideShow: {
    sorting: {
      heading: 'Sorting and aligning of goals via drag and drop',
      description: 'Manually sort and realign goals or whole OKR sets by clicking the drag handle to the left and moving it to the desired position.',
    },
    properties: {
      heading: 'Customize anything with properties',
      description: 'Properties are one of the most powerful features of Mooncamp, allowing your organization to completely customize the Mooncamp platform to fit your needs, internal processes, and company language.',
      link: '@:{\'general.helpCenterBaseUrl\'}/properties',
    },
    bulkEdit: {
      heading: 'Bulk-edit goals',
      description: 'Easily edit multiple goals by selecting them with your mouse and performing a right-click.',
    },
    expand: {
      heading: 'Collapse or expand all goals',
      description: 'Use “right-click” and select “Expand/collapse all” to quickly collapse or expand all goals on the table view.',
    },
    okrRules: {
      heading: 'Use rules to adapt the goal system to your processes',
      description: 'You can not only create your own goal types (such as Objectives, Key Results, or Initiatives) but also customize them with rules you define yourself (e.g., “Key Results may only be aligned to Objectives and need to have an owner”).',
      link: '@:{\'general.helpCenterBaseUrl\'}/goal-rules',
    },
  },
  goalCard: {
    addProperty: 'Add {label}',
    summary: {
      comments: 'No comments | One comment | {count} comments',
      lastCommentDate: 'Last comment: {date}',
      updates: 'No updates | One update | {count} updates',
      lastUpdateDate: 'Last update: {date}',
    },
  },
  gridPage: {
    error: { loading: 'Error while loading content.' },
    header: {
      addDescription: 'Add description',
      addADescription: 'Add a description...',
    },
    empty: {
      title: 'Add content to get started.',
      readOnly: 'This dashboard is empty and you don\'t have access to add content.',
      helpLink: '@:{\'general.helpCenterBaseUrl\'}/dashboards',
      learnMore: 'Learn more about dashboards and use cases',
    },
    gridActions: {
      chart: 'Chart',
      singleGoalProgress: 'Goal progress',
      text: 'Text',
      goalList: 'Goals',
      checkinList: 'Check-ins',
      updateFeedList: 'Update feed',
      memberList: 'Members',
      addTooltip: 'Add content to row',
      iframe: 'Embed',
      media: 'Media',
      goalProgressChart: 'Goal progress',
      statusDistribution: 'Status distribution',
      statusScore: 'Status score',
      goalsByStatus: 'Goals by status',
      customChart: 'Custom chart',
      lists: 'Lists',
    },
    addRowBtn: { addRow: 'Add content' },
    tile: {
      header: {
        editTitle: 'Edit title',
        addTitle: 'Add title',
        editDescription: 'Edit description',
        addDescription: 'Add description',
        showBorder: 'Show border',
        duplicate: 'Duplicate',
        compareTo: {
          base: 'Compared to {relativeDate}',
          one_day_ago: 'yesterday',
          previous_week: 'last week',
          previous_month: 'last month',
        },
        singleGoal: {
          settings: 'Settings',
          openGoal: 'Open goal',
        },
      },
    },
    duplicate: { titlePrefix: 'Duplicate of' },
    gridPageTileMedia: {
      uploadImageOrEmbedVideo: 'Upload an image or embed a video',
      uploadImage: 'Upload image',
      embedVideoLink: 'Embed video link',
      uploadFile: 'Upload file',
      pasteVideoLink: 'Paste the video link...',
      embedVideo: 'Embed video',
      supportedPlatforms: 'Works with YouTube, Loom, and more',
    },
  },
  gridPageSlideShow: {
    general: {
      heading: 'Dashboards',
      description: 'Custom dashboards can have various purposes, like providing an executive summary encapsulating a company\'s core principles, offering in-depth quarterly analyses, highlighting weekly management details, or giving a comprehensive snapshot of a department\'s key functions and goals.',
    },
    addContentActions: {
      heading: 'Adding content',
      description: 'Content is organized into <b>rows</b>. To add a <b>card</b> to a row, hover over it and click the "+" button on the left. To add a new row, click the "+ Add content" button below the last  row in the dashboard. Cards can be anything: a text or image, a list of goals, check-ins, updates, or members, and even goal progress metrics or KPIs that you defined.',
    },
    draggingActions: {
      heading: 'Moving and resizing',
      description: 'To <b>move or resize</b> cards within a row, simply click and hold the mouse in the top part of a card, and drag it to its new position. Cards will automatically fill the width of a row. You can adjust their width and height by clicking and dragging the control between them. Simply hover between the cards to access the drag control.',
    },
  },
  treeItem: { noTypeAllowed: 'No allowed goal type found' },
  cardWidthSelector: {
    title: 'Card size',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
  },
  appFeedbackDropdown: {
    feedback: 'Feedback',
    tree: { label: 'How can we improve the Goal Tree view?' },
    customPages: { label: 'How can we improve the Custom Dashboards feature?' },
    goalStatus: { label: 'How can we improve the Goal status feature?' },
    customCharts: { label: 'How can we improve our custom charts?' },
    general: { label: 'Your feedback' },
    placeholder: 'What do you like or dislike...',
    success: 'Thank you for your feedback!',
  },
  gridPageSingleGoal: {
    goal: { expected: 'Expected value:' },
    empty: { noGoal: 'No goal selected' },
  },
  gridPageTileSingleGoalEditor: {
    title: 'Progress from goal',
    goalPicker: 'Selected goal',
    goalPickerPlaceholder: 'Select a goal',
    visualizeAs: 'Display as',
    compareTo: 'Compare to',
    dateRange: 'Time frame',
  },
  gridPageTileSingleGoalType: {
    number: 'Metric',
    line: 'Line chart',
  },
  gridPageList: {
    systemDashboards: '{title} progress and health',
    progress: {
      title: 'Progress dashboard',
      subtitle: 'An overview of your progress towards your goals.',
    },
    health: {
      title: 'Health dashboard',
      subtitle: 'A complete health and quality check on all of your organization’s goals.',
    },
    customDashboards: 'Custom dashboards',
    columns: {
      title: 'Name',
      access: 'Access',
      creator: 'Created by',
      createdAt: 'Created on',
    },
    privacy: {
      private: 'Private',
      public: 'Shared',
      all: 'All',
    },
    noAllowanceToCreate: 'You are not allowed to create more private items.',
  },
  allowanceAlert: {
    messagePrivate: 'Your workspace has created {pagesCount} out of {pagesAllowance} private dashboards.',
    messagePublic: 'Your workspace has created {pagesCount} out of {pagesAllowance} shared dashboards.',
    subMessage: 'Unlock more by upgrading your plan.',
    action: 'Unlock more',
  },
  gridPagesContextMenu: {
    cannotDeleteMultiple: 'You are not allowed to delete the selected items.',
    deleteHint: 'You are not allowed to delete all of the selected items.',
    cannotEditMultiple: 'You are not allowed to edit the selected items.',
    cannotMakePublicMultiple: 'You are not allowed to have more shared items.',
    duplicateHint: 'You are not allowed to duplicate all of the selected items because some are shared.',
    cannotDuplicateAll: 'You are not allowed to duplicate all of the selected items.',
    changeAccess: 'Edit access rights',
    makePublicHint: 'You are not allowed to share all selected items.',
    changeAccessHint: 'You are not allowed to edit the access rights of all selected items.',
  },
  gridPagesShareDropdown: {
    creatorDescription: 'Creator of this dashboard',
    cannotShareNoAllowance: 'You are not allowed to have more shared items.',
  },
  addToGridEditor: {
    selectPage: 'Select Dashboard',
    successAction: 'Go to dashboard',
  },
  goalTree: {
    fitToScreen: 'Fit to screen',
    disabledView: 'This view is disabled.',
    empty: {
      hasFilter: 'No filter results.',
      noFilter: 'Empty.',
      cta: 'Click to add a goal.',
    },
  },
  treeViewSlideShow: {
    helpCenterLink: '@:{\'general.helpCenterBaseUrl\'}/goal-tree',
    navigation: {
      heading: 'Scrolling and zooming',
      description: `<ul>
<li>To scroll vertically, simply use your <b>scroll wheel</b> on the mouse.</li>
<li>To scroll horizontally, hold the <b>Shift key</b> on your keyboard and then use the <b>scroll wheel</b>.</li>
<li>To zoom in or out, press and hold the <b>CMD key</b> or <b>Ctrl key</b>, then use the <b>scroll wheel</b>.</li>
        </ul>`,
    },
    edit: {
      heading: 'Edit and realign goals',
      description: `<ul>
<li>To edit goals without leaving this page click the edit icon on the top right corner of a goal card.</li>
<li>To realign a goal, simply drag and drop the desired goal to a new location.</li>
        </ul>`,
    },
    viewControls: {
      heading: 'View controls',
      description: `<ul><li>Show and hide properties</li>
        <li>Change the card width to make the tree view more condense or extensive</li></ul>`,
    },
    handAndSelectTool: {
      heading: 'Select and hand tool',
      description: `<ul><li>Use the select tool to realign or to perform bulk actions.</li>
        <li>Use the hand tool to navigate content by pressing the left mouse button and moving the mouse.</li></ul>`,
    },
  },
  trashItem: { deletedAt: 'Deleted on' },
  goalStatusSettings: {
    name: 'Name',
    archived: 'Archived options',
    cannotDeletePermanently: 'Cannot delete option as it is still in use.',
    scoreTooltip: 'The threshold is used to automatically select a status based on a goal\'s time-weighted progress. Time-weighted progress measures how far a goal has advanced relative to the time that has elapsed. If the time-weighted progress surpasses the threshold, the system recommends the status closest to that progress. Closed statuses are never recommended.',
    defaultFlagToolTip: 'This flag indicates that the status will be automatically selected if the goal has not been updated yet.',
    title: 'Create a new status...',
  },
  statusOptionEditor: {
    color: 'Color',
    setAsDefault: 'Set as default',
    group: 'Group',
    score: 'Threshold',
    cannotArchiveDefaultOption: 'Cannot archive default options.',
  },
  statusChartSettingsDropdown: {
    title: 'Chart type',
    average: 'Average',
    distribution: 'Distribution',
  },
  dashboards: {
    list: 'List',
    reports: 'Reports',
    progressDashboard: {
      helpText: 'Learn more about the Progress Dashboard',
      helpLink: '@:{\'general.helpCenterBaseUrl\'}/progress-dashboard',
    },
    healthDashboard: {
      helpText: 'Learn more about the Health Dashboard',
      helpLink: '@:{\'general.helpCenterBaseUrl\'}/health-dashboard',
    },
  },
  goalProgressMenu: {
    progressDisplayOptions: {
      [progressDisplayOption.absolute]: 'Absolute',
      [progressDisplayOption.relative]: 'Relative',
      [progressDisplayOption.full]: 'Both',
    },
    optionLabel: 'Display',
  },
  gridPageTileIframe: {
    empty: 'Embed anything (Power BI, Tableau, Excel, PPT, Google Docs, Youtube, ...)',
    hint: 'Paste a URL or embed code here to include content from external sources',
    embedLink: 'Embed link',
    placeholder: 'Please insert: https://...',
    embedProviderWarning: 'This looks like a regular {provider} link, which {provider} does not allow to be embedded directly. In {provider}, look for an "Embed" or "Publish to web" option and paste that dedicated embed link here instead.',
    embedBlockedTitle: 'This {provider} link can’t be embedded',
  },
  reportsTable: {
    scheduleReport: 'Schedule a report',
    recipients: 'Recipients',
    schedule: 'Cadence',
    lastUpdatedAt: 'Last updated',
    createdBy: 'Created by',
    deletePrompt: 'Are you sure you want to delete this report?',
    pause: 'Pause',
    activate: 'Activate',
    subscribed: 'Subscribed',
    subject: 'Name',
    printable: 'Dashboard',
    incompleteSetup: 'Setup of this report is not complete. It will not be sent.',
    planRestricted: 'This report\'s dashboard is not available on your current plan. It will not be sent.',
  },
  reportEditor: {
    dashboard: 'Dashboard',
    schedule: 'Cadence',
    addRecipient: 'Add recipient',
    recipients: 'Recipients',
    invalidEmail: 'Invalid email',
    alreadyAdded: 'Email already added',
    subject: 'Subject',
    message: 'Message (optional)',
    exportFormat: 'Export format',
    width: 'Export layout',
    pdfWidthNarrow: 'Narrow (A4)',
    pdfWidthStandard: 'Standard',
    pdfWidthWide: 'Wide',
    pdfWidthExtraWide: 'Extra wide',
    tooManyRecipients: 'You can add up to 50 recipients.',
    tooManyRecipientsTrial: 'Please upgrade your workspace to add more recipients.',
    privateDashboardRecipients: 'You can send private dashboards only to yourself.',
    [printablePage.healthDashboard]: 'Health Dashboard',
    [printablePage.progressDashboard]: 'Progress Dashboard',
    dashboardEmpty: 'Not set',
  },
  dashboardDetails: {
    manageReports: 'Manage reports',
    scheduleReport: 'Schedule',
  },
  gridPageChartEditor: {
    title: 'Data',
    source: 'Source',
    goal: 'Goals',
    checkin: 'Check-ins',
    goalActivity: 'Goal updates',
    filter: 'Filter',
    cycle: 'Cycle',
    cycleCount: '{count} cycles',
    chart: 'Chart type',
    xAxis: 'X axis',
    yAxis: 'Y axis',
    whatToShow: 'What to show',
    groupBy: 'Group by',
    groupBySection: 'Split by',
    count: 'Count',
    none: 'None',
    representation: 'Every part represents',
    pie: 'Donut',
    bar: 'Horizontal bar',
    column: 'Vertical bar',
    line: 'Line',
    metric: 'Metric',
    table: 'Table',
    titlePlaceholder: 'Enter title...',
    descriptionPlaceholder: 'Enter description...',
    addDescription: 'Add description',
    data: 'Data',
    metricSection: 'Metric',
    columns: 'Columns',
    values: 'Values',
    bucket: 'Bucket',
    closePrompt: 'Discard changes?',
    excludeNullValues: 'Exclude null values',
    boundedProgress: 'Cap progress at 100%',
    boundedProgressTooltip: 'Caps each goal’s progress at 100% before it is aggregated. Turn off to include progress above 100%, which can pull the result higher.',
    stackSeries: 'Stack series',
    unassignedValue: 'No value',
    unassignedEdge: 'No {edge}',
    overTime: 'Time Series',
    snapshot: 'Snapshot',
    progressOverTime: 'Progress over time',
    statusScoreOverTime: '{property} score over time',
    statusDistributionOverTime: '{property} distribution over time',
    statusScore: '{property} score',
    statusDistribution: '{property} distribution',
    timeRange: 'Time range',
    interval: 'Interval',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    chartTypeDisabledForTimeSeries: 'Only line charts are supported for time series metrics',
    userGroupingDisabled: 'User grouping is disabled in workspace settings.',
    userPropertyDisabled: 'Person properties cannot be used here',
    style: 'Style',
    colorScheme: 'Color',
    colorSchemeAutomatic: 'Auto',
    colorSchemeColorful: 'Colorful',
    colorSchemeGrey: 'Grey',
    colorSchemeBrown: 'Brown',
    colorSchemeYellow: 'Yellow',
    colorSchemeOrange: 'Orange',
    colorSchemeRed: 'Red',
    colorSchemePink: 'Pink',
    colorSchemePurple: 'Purple',
    colorSchemeBlue: 'Blue',
    colorSchemeGreen: 'Green',
    precision: 'Decimal places',
    precisionAutomatic: 'Automatic',
    referenceLines: 'Reference lines',
    addReferenceLine: 'Add reference line',
    referenceLine: {
      value: 'Value',
      label: 'Label',
      style: 'Style',
      color: 'Color',
      solid: 'Solid',
      dash: 'Dashed',
      dot: 'Dotted',
      description: 'Add reference lines to set goals, mark thresholds, and make your charts clearer.',
    },
    sortBy: 'Sort',
    sortAscending: 'Ascending (A \u2192 Z)',
    sortDescending: 'Descending (Z \u2192 A)',
    sortCountAscending: 'Count ascending',
    sortCountDescending: 'Count descending',
    sortOldToNew: 'Old \u2192 New',
    sortNewToOld: 'New \u2192 Old',
    sortManual: 'Manual',
    categories: 'Categories',
    series: 'Series',
    metricTitle: 'Title',
    metricTitlePlaceholder: 'e.g. Goals at risk',
    hideAll: 'Hide all',
    showAll: 'Show all',
  },
  metricColorEditor: {
    color: 'Color',
    dynamicColor: 'Dynamic color',
    colorLabel: 'Color',
    operator: 'Operator',
    value: 'Value',
    addRule: 'Add rule',
    whenValue: 'If value',
    operators: {
      greaterThan: 'Greater than',
      greaterThanOrEqual: 'Greater than or equal',
      lessThan: 'Less than',
      lessThanOrEqual: 'Less than or equal',
      equal: 'Equal',
    },
    colors: {
      default: 'Default',
      grey: 'Grey',
      brown: 'Brown',
      orange: 'Orange',
      yellow: 'Yellow',
      green: 'Green',
      blue: 'Blue',
      purple: 'Purple',
      pink: 'Pink',
      red: 'Red',
    },
  },
  detailHeader: {
    close: 'Close',
    openAsPage: 'Open as page',
    switchPeekMode: 'Switch peek mode',
  },
  groupByOption: {
    day: 'Day',
    week: 'Week',
    month: 'Month',
    quarter: 'Quarter',
    year: 'Year',
    unique: 'Unique values',
    none: 'None',
    bucket: 'Buckets',
    sum: 'Sum',
    avg: 'Average',
    min: 'Minimum',
    max: 'Maximum',
    count: 'Count',
    count_distinct: 'Unique values',
  },
  gridPageChart: {
    count: 'Count',
    goal: 'Goals',
    update: 'Check-ins',
    goalActivity: 'Goal updates',
    countDistinct: 'Unique values of {edge}',
    unique: 'Unique values of {edge}',
    sum: 'Sum of {edge}',
    avg: 'Average of {edge}',
    min: 'Minimum of {edge}',
    max: 'Maximum of {edge}',
    total: 'Total',
    dataLimitCategories: 'Showing {shown} of {total} categories',
    dataLimitCategoriesAndSeries: 'Showing {shown} of {total} categories and {shownSeries} of {totalSeries} series',
    dataLimitTooltip: 'A chart can display a maximum of 200 categories and 50 series. Use filters to narrow down the data.',
    orphanRefMessage: 'Something is wrong with your chart data',
    orphanRefHelp: 'Help me fix',
    deletedProperty: 'Deleted property',
    deletedPropertyTooltip: 'The property used here has been deleted. Pick another property or remove this setting to fix the chart.',
  },
  bucketSelector: {
    bucketType: 'Grouping',
    none: 'None',
    automatic: 'Automatic',
    custom: 'Custom',
    minValue: 'Min. value',
    maxValue: 'Max. value',
    bucketSize: 'Group size',
    minValueError: 'The min. value must be less than the max. value.',
    bucketSizeError: 'The group size must be greater than 0.',
    tooManyBucketsError: 'The current settings would create too many groups. Please use a larger group size or a smaller range (max. {max} groups).',
  },
  noAccessPage: {
    title: 'You don\'t have access to this {entityName}',
    buttonText: 'Back to your content',
  },
  gridPageChartDataTable: {
    value: 'Value',
    metric: 'Metric',
    groupBy: 'Group by',
    date: 'Date',
    actual: 'Actual',
    expected: 'Expected',
  },
  savedViewEditor: { layout: 'Layout' },
};

export default mergeLangObjects(globalLang, lang);
